import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import apiService from '../../../services/apiService';
import { useAuthStore } from '../../../stores/authStore';
import { shortLocation } from '../../../utils/locationDisplay';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Modal,
  Form,
  message,
  Descriptions,
  Tabs,
  Statistic,
  Badge,
  Tooltip,
  InputNumber,
  Divider,
  Alert,
  Popconfirm,
  Menu,
  Checkbox,
  Dropdown,
  Timeline,
  Radio,
} from 'antd';
import {
  StockOutlined,
  EyeOutlined,
  EditOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined,
  BarChartOutlined,
  InboxOutlined,
  SendOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  AuditOutlined,
  UserOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UpOutlined,
  DownOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import dayjs from 'dayjs';
import useDraggableColumns from '../../../components/table/useDraggableColumns';
import { useTranslation } from "react-i18next";
import { useStatusLabel } from '../../../i18n/statusLabel';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// 各倉庫庫存明細接口
interface WarehouseInventory {
  id: string;
  warehouseId: string;
  warehouseName: string;
  total: number;
  available: number;
  shipping: number;
  locationCode?: string;
  updatedAt: string;
}

// 貨位分佈明細接口
interface LocationDetail {
  orderNumber: string;
  lotNumber: string;
  expirationDate?: string;
  locationPath: string;
  quantity: number;
  updatedAt: string;
}

// 庫存數據接口 - 基於Excel字段
interface StockItem {
  id: string;
  ownerAccount: string; // 貨主賬號
  sku: string;
  barcode: string;
  productName: string; // 產品名稱
  updateTime: string; // 更新時間
  stockQuantity: number; // 庫存數量（總計）
  availableQuantity: number; // 可銷售數量 Goods can be sold
  oversellDebt?: number; // 超售欠貨（已確認超售、尚未補回的數量）
  pendingReplacementQty?: number; // 補發中（補發單已建立、尚未處理的數量）
  totalOrderedQuantity?: number; // 總下單數量（PENDING訂單總量）
  outboundQuantity: number; // 出庫中數量
  expectedQuantity: number; // 預期數量（來自入庫單）
  occupiedCBM: number; // 佔用庫存CBM
  lastInboundOrder: string; // 最後修改-入庫單
  lastInboundQuantity: number; // 最後修改-入庫數量
  lastOutboundOrder: string; // 最後修改-發貨單
  lastOutboundQuantity: number; // 最後修改-發貨數量
  
  // 額外字段
  warehouse: string; // 保留用於調整申請
  category: string;
  unitPrice: number;
  totalValue: number;
  reorderLevel: number;
  supplier: string;
  status: 'normal' | 'low_stock' | 'out_of_stock' | 'excess';
  alternateSkuList?: string[]; // 備用 SKU 列表
  
  // 各倉庫庫存明細（詳情頁使用）
  warehouses?: WarehouseInventory[];
}

// 相關訂單接口
interface RelatedOrder {
  id: string;
  orderNumber: string;
  type: 'inbound' | 'outbound' | 'transfer';
  date: string;
  quantity: number;
  status: string;
  reference?: string;
  location?: string;      // 貨位
  lotNumber?: string;     // 批次號
  expiryDate?: string;    // 到期日
}

// 庫存調整接口
interface StockAdjustment {
  id: string;
  reference: string;  // ADJ ID, e.g., ADJ-20251211-062155-527-846
  sku: string;
  productName: string;
  currentQuantity: number;
  adjustmentQuantity: number;
  newQuantity: number;
  reason: string;
  reasonCode: string;
  notes?: string;
  requestedBy: string;
  requestedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  status: 'pending' | 'approved' | 'rejected';
  approvalReason?: string;
  warehouse: string;
  location: string;
  urgency: 'low' | 'normal' | 'high' | 'critical';
  alternateSkuList?: string[]; // 添加備用 SKU 列表
}

// 表格列配置類型
type ColumnSetting = { visible: boolean; order: number };
type StockColumnKey = 'sku' | 'productName' | 'barcode' | 'stockQuantity' | 'availableQuantity' | 'totalOrderedQuantity' | 'outboundQuantity' | 'expectedQuantity' | 'occupiedCBM' | 'lastInboundOrder' | 'lastOutboundOrder' | 'updateTime' | 'status' | 'actions';
type StockColumnSettings = Record<StockColumnKey, ColumnSetting>;
type AdjustmentColumnKey = 'reference' | 'sku' | 'productName' | 'currentQuantity' | 'adjustmentQuantity' | 'newQuantity' | 'reason' | 'notes' | 'requestedBy' | 'requestedAt' | 'reviewedBy' | 'reviewedAt' | 'urgency' | 'status' | 'actions';
type AdjustmentColumnSettings = Record<AdjustmentColumnKey, ColumnSetting>;

const StockManagement: React.FC = () => {
    const { t } = useTranslation();
    const statusLabel = useStatusLabel();
  // 獲取當前用戶信息
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isCustomerRole = user?.role === 'CUSTOMER';

  // 狀態管理
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  // 先定義排序狀態，避免被閉包在初始化前引用
  const [stockSortField, setStockSortField] = useState<string>('');
  const [stockSortOrder, setStockSortOrder] = useState<'asc' | 'desc' | ''>('');
  // 受控分頁狀態（對齊 Products 實現）
  const [stockCurrentPage, setStockCurrentPage] = useState<number>(1);
  const [stockPageSize, setStockPageSize] = useState<number>(20);
  const [stockTotal, setStockTotal] = useState<number>(0);
  // 篩選狀態（需在 loadStock 之前定義，避免 TDZ）
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  // Customer and store code filters (use name instead of ID since some customers have null IDs)
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [selectedStoreCode, setSelectedStoreCode] = useState<string>('');
  // Optional "as of date" for historical inventory exports (month-end billing, audit, lot availability)
  const [asOfDate, setAsOfDate] = useState<dayjs.Dayjs | null>(null);
  // Optional time of day. Left empty the export behaves exactly as before —
  // the nightly snapshot for that date, lot and location included. Filled in,
  // the position is traced back to that exact second instead, which the client
  // asked for so they can tell which moment a file represents.
  const [asOfTime, setAsOfTime] = useState<dayjs.Dayjs | null>(null);
  // Stock-take mode for the export. Off by default: the normal report is
  // unchanged. On, the sheet also carries the stock that exists only in the
  // aggregate, which a count list built from batch records would never show.
  const [includeAggregateRemainder, setIncludeAggregateRemainder] = useState(false);
  const [customersWithInventory, setCustomersWithInventory] = useState<Array<{ id: string | null; name: string }>>([]);
  const [storeCodesWithInventory, setStoreCodesWithInventory] = useState<Array<{ storeCode: string; customerName: string }>>([]);
  const locationHook = useLocation();

  // Sequence guard: bumped on every call to loadStock. Async responses that
  // don't match the latest sequence get dropped instead of overwriting the
  // freshest UI state. Guards against the "search returns blank" race caused
  // by overlapping fetches — fast typing / debounce + mount-effect fired two
  // requests, the older one arriving last used to clobber the correct result.
  const loadStockSeqRef = useRef(0);

  // 從後端拉取庫存列表
  const loadStock = useCallback(async (page = 1, limit = 20) => {
    const seq = ++loadStockSeqRef.current;
    try {
      setLoading(true);
      const resp: any = await apiService.getStockList({
        page,
        limit,
        sortField: stockSortField || 'updatedAt',
        sortOrder: (stockSortOrder as any) || 'desc',
        search: (searchText || '').trim() || undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        customerName: selectedCustomerName || undefined,
        storeCode: selectedStoreCode || undefined,
      });
      if (seq !== loadStockSeqRef.current) return; // stale response — a newer fetch has already started
      const list = (resp?.data || []).map((r: any) => ({
        id: r.id,
        ownerAccount: r.supplier || t('Default'),
        sku: r.itemCode,
        barcode: r.barcode || '',
        productName: r.itemName,
        updateTime: r.updatedAt,
        stockQuantity: Number(r.total || 0),
        availableQuantity: Number(r.available || 0),
        oversellDebt: Number(r.oversellDebt || 0),
        pendingReplacementQty: Number(r.pendingReplacementQty || 0),
        totalOrderedQuantity: Number(r.totalOrderedQuantity || 0),
        outboundQuantity: Number(r.shipping || 0),
        expectedQuantity: Number(r.expectedQuantity || r.pendingInQuantity || 0),
        occupiedCBM: 0,
        lastInboundOrder: '',
        lastInboundQuantity: 0,
        lastOutboundOrder: '',
        lastOutboundQuantity: 0,
        warehouse: '', // 列表不显示仓库名称
        category: r.category || '',
        unitPrice: 0,
        totalValue: 0,
        reorderLevel: 0,
        supplier: r.supplier || '',
        status: Number(r.total || 0) === 0 ? 'out_of_stock' : (Number(r.available || 0) <= 0 ? 'low_stock' : 'normal'),
        alternateSkuList: Array.isArray(r.alternateSkuList) ? r.alternateSkuList : []  // 添加備用 SKU 列表
      }));
      setStockData(list);
      const total = resp?.pagination?.total ?? list.length;
      setStockTotal(total);
      setStockCurrentPage(page);
      setStockPageSize(limit);
    } catch (e) {
      if (seq !== loadStockSeqRef.current) return;
      setStockData([]);
    } finally {
      if (seq === loadStockSeqRef.current) setLoading(false);
    }
  }, [stockSortField, stockSortOrder, searchText, selectedCustomerName, selectedStoreCode, selectedStatus]);

  // Mount-only: read the URL ?sku= param and seed searchText. Do NOT call
  // loadStock here — the debounced search effect below fires on mount too
  // (its deps have initial values) and will do the initial fetch after 300ms.
  // Calling loadStock inline used to race with that debounced fetch, dropping
  // the correct result underneath a stale one.
  useEffect(() => {
    try {
      const usp = new URLSearchParams(locationHook.search);
      const skuParam = usp.get('sku');
      if (skuParam && skuParam.trim().length > 0) {
        setSearchText(skuParam.trim());
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHook.search]);

  // Load customer and store code filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        // Load customers with inventory
        const customersResp: any = await apiService.getCustomersWithInventory();
        if (customersResp?.success && customersResp?.data) {
          setCustomersWithInventory(customersResp.data);
        }
        // Load store codes with inventory
        const storeCodesResp: any = await apiService.getStoreCodesWithInventory();
        if (storeCodesResp?.success && storeCodesResp?.data) {
          setStoreCodesWithInventory(storeCodesResp.data);
        }
      } catch (e) {
        console.error('Failed to load filter options:', e);
      }
    };
    loadFilterOptions();
  }, []);

  // 搜尋/排序/篩選變更時，防抖請求後端
  const debounceRef = useRef<any>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadStock(1, stockPageSize);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText, stockSortField, stockSortOrder, stockPageSize, selectedCustomerName, selectedStoreCode]);
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [_detailsLoading, setDetailsLoading] = useState(false); // Used for loading state during fetch
  const [adjustmentVisible, setAdjustmentVisible] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  // 庫存調整申請 - 按位置調整 (新功能)
  const [stockLocations, setStockLocations] = useState<Array<{
    inboundItemId: string;
    locationId: string;
    locationCode: string;
    locationName: string;
    warehouseId: string;
    warehouseName: string;
    currentStock: number;
    lotNumber: string | null;
    expiryDate: string | null;
  }>>([]);
  const [locationAdjustments, setLocationAdjustments] = useState<Record<string, number>>({});
  const [relatedOrders, setRelatedOrders] = useState<RelatedOrder[]>([]);
  const [relatedOrdersLoading, setRelatedOrdersLoading] = useState(false);
  const [relatedOrdersPage, setRelatedOrdersPage] = useState(1);
  const [relatedOrdersPageSize, setRelatedOrdersPageSize] = useState(20);
  const [relatedOrdersTotal, setRelatedOrdersTotal] = useState(0);
  const [relatedOrdersType, setRelatedOrdersType] = useState<'all' | 'outbound' | 'inbound' | 'adjustment'>('all');
  const [productLocations, setProductLocations] = useState<LocationDetail[]>([]);
  const [adjustmentForm] = Form.useForm();
  // 添加報告日期範圍選擇彈窗狀態
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportForm] = Form.useForm();
  // 本地回顯緩存：按庫存項ID保存最近一次編輯內容
  const [lastEdits, setLastEdits] = useState<Record<string, { adjustmentQuantity: number; reason: string; notes: string; newQuantity: number; currentQuantity: number }>>({});

  // 一鍵清零 - 批量選擇庫存項
  const [selectedStockIds, setSelectedStockIds] = useState<React.Key[]>([]);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // 庫存表格排序狀態（已提前定義）

  // 調整申請表格排序狀態
  const [adjustmentSortField, setAdjustmentSortField] = useState<string>('');
  const [adjustmentSortOrder, setAdjustmentSortOrder] = useState<'asc' | 'desc' | ''>('');

  // 庫存表格列配置狀態
  const [stockColumnSettings, setStockColumnSettings] = useState<StockColumnSettings>(() => {
    const saved = localStorage.getItem('stock-management-columns-config');
    const version = localStorage.getItem('stock-management-columns-version');
    
    // 檢查版本，如果不是最新版本則重置配置
    if (version !== '2024-01-v6' || !saved) {
      const defaultConfig: StockColumnSettings = {
        sku: { visible: true, order: 0 },
        productName: { visible: true, order: 1 },
        barcode: { visible: true, order: 2 },
        stockQuantity: { visible: false, order: 3 },
        availableQuantity: { visible: true, order: 4 },
        totalOrderedQuantity: { visible: true, order: 5 },
        outboundQuantity: { visible: true, order: 6 },
        expectedQuantity: { visible: true, order: 7 },
        occupiedCBM: { visible: false, order: 8 },
        lastInboundOrder: { visible: false, order: 9 },
        lastOutboundOrder: { visible: false, order: 10 },
        updateTime: { visible: true, order: 11 },
        status: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      localStorage.setItem('stock-management-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('stock-management-columns-version', '2024-01-v6');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved) as StockColumnSettings;
    } catch {
      const defaultConfig: StockColumnSettings = {
        sku: { visible: true, order: 0 },
        productName: { visible: true, order: 1 },
        barcode: { visible: true, order: 2 },
        stockQuantity: { visible: false, order: 3 },
        availableQuantity: { visible: true, order: 4 },
        totalOrderedQuantity: { visible: true, order: 5 },
        outboundQuantity: { visible: true, order: 6 },
        expectedQuantity: { visible: true, order: 7 },
        occupiedCBM: { visible: false, order: 8 },
        lastInboundOrder: { visible: false, order: 9 },
        lastOutboundOrder: { visible: false, order: 10 },
        updateTime: { visible: true, order: 11 },
        status: { visible: true, order: 12 },
        actions: { visible: true, order: 13 }
      };
      return defaultConfig;
    }
  });

  // 調整申請表格列配置狀態
  const [adjustmentColumnSettings, setAdjustmentColumnSettings] = useState<AdjustmentColumnSettings>(() => {
    const saved = localStorage.getItem('adjustment-audit-columns-config');
    const version = localStorage.getItem('adjustment-audit-columns-version');

    const defaultConfig: AdjustmentColumnSettings = {
      reference: { visible: true, order: 0 },
      sku: { visible: true, order: 1 },
      productName: { visible: true, order: 2 },
      currentQuantity: { visible: true, order: 3 },
      adjustmentQuantity: { visible: true, order: 4 },
      newQuantity: { visible: true, order: 5 },
      reason: { visible: true, order: 6 },
      notes: { visible: false, order: 7 },
      requestedBy: { visible: true, order: 8 },
      requestedAt: { visible: true, order: 9 },
      reviewedBy: { visible: false, order: 10 },
      reviewedAt: { visible: false, order: 11 },
      urgency: { visible: true, order: 12 },
      status: { visible: true, order: 13 },
      actions: { visible: true, order: 14 }
    };

    // 檢查版本，如果不是最新版本則重置配置
    // Also check if 'reference' key exists in saved config (for migration)
    const needsReset = version !== '2026-02-v7' || !saved;

    if (needsReset) {
      localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('adjustment-audit-columns-version', '2026-02-v7');
      // Also clear the draggable columns storage to force column order reset
      // The useDraggableColumns hook stores order with ::order suffix
      localStorage.removeItem('inventory-adjustment-table');
      localStorage.removeItem('inventory-adjustment-table::order');
      return defaultConfig;
    }

    try {
      const parsed = JSON.parse(saved) as AdjustmentColumnSettings;
      // If saved config is missing the 'reference' key, use default and clear draggable storage
      if (!parsed.reference) {
        localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(defaultConfig));
        localStorage.setItem('adjustment-audit-columns-version', '2026-02-v7');
        localStorage.removeItem('inventory-adjustment-table');
        localStorage.removeItem('inventory-adjustment-table::order');
        return defaultConfig;
      }
      return parsed;
    } catch {
      return defaultConfig;
    }
  });

  // Force clear localStorage on mount if reference column is missing (one-time migration)
  useEffect(() => {
    const version = localStorage.getItem('adjustment-audit-columns-version');
    if (version !== '2026-02-v7') {
      const defaultConfig: AdjustmentColumnSettings = {
        reference: { visible: true, order: 0 },
        sku: { visible: true, order: 1 },
        productName: { visible: true, order: 2 },
        currentQuantity: { visible: true, order: 3 },
        adjustmentQuantity: { visible: true, order: 4 },
        newQuantity: { visible: true, order: 5 },
        reason: { visible: true, order: 6 },
        notes: { visible: false, order: 7 },
        requestedBy: { visible: true, order: 8 },
        requestedAt: { visible: true, order: 9 },
        reviewedBy: { visible: false, order: 10 },
        reviewedAt: { visible: false, order: 11 },
        urgency: { visible: true, order: 12 },
        status: { visible: true, order: 13 },
        actions: { visible: true, order: 14 }
      };
      localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('adjustment-audit-columns-version', '2026-02-v7');
      localStorage.removeItem('inventory-adjustment-table');
      localStorage.removeItem('inventory-adjustment-table::order');
      setAdjustmentColumnSettings(defaultConfig);
      console.log('[StockManagement] Migrated adjustment columns to v7 with reference column');
    }
  }, []);

  // 處理列排序
  const handleStockColumnSort = (columnKey: string) => {
    if (stockSortField === columnKey) {
      setStockSortOrder(stockSortOrder === 'asc' ? 'desc' : stockSortOrder === 'desc' ? '' : 'asc');
      if (stockSortOrder === 'desc') {
        setStockSortField('');
      }
    } else {
      setStockSortField(columnKey);
      setStockSortOrder('asc');
    }
  };

  // 獲取排序圖標
  const getStockSortIcon = (columnKey: string) => {
    if (stockSortField !== columnKey) return null;
    return stockSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 處理調整申請列排序
  const handleAdjustmentColumnSort = (columnKey: string) => {
    if (adjustmentSortField === columnKey) {
      setAdjustmentSortOrder(adjustmentSortOrder === 'asc' ? 'desc' : adjustmentSortOrder === 'desc' ? '' : 'asc');
      if (adjustmentSortOrder === 'desc') {
        setAdjustmentSortField('');
      }
    } else {
      setAdjustmentSortField(columnKey);
      setAdjustmentSortOrder('asc');
    }
  };

  // 獲取調整申請排序圖標
  const getAdjustmentSortIcon = (columnKey: string) => {
    if (adjustmentSortField !== columnKey) return null;
    return adjustmentSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };
  
  // Stock Adjustment相關狀態
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [selectedAdjustment, setSelectedAdjustment] = useState<StockAdjustment | null>(null);
  const [adjustmentDetailsVisible, setAdjustmentDetailsVisible] = useState(false);
  const [approvalVisible, setApprovalVisible] = useState(false);
  const [approvalForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('stock');
  // 列設置下拉菜單可見狀態
  const [stockColumnSettingsVisible, setStockColumnSettingsVisible] = useState(false);
  // 調整審核分頁狀態
  const [adjustmentCurrentPage, setAdjustmentCurrentPage] = useState<number>(1);
  const [adjustmentPageSize, setAdjustmentPageSize] = useState<number>(20);
  const [adjustmentTotal, setAdjustmentTotal] = useState<number>(0);
  // 調整審核篩選
  const [adjustmentSearchSku, setAdjustmentSearchSku] = useState<string>('');
  const [adjustmentStatus, setAdjustmentStatus] = useState<string>('all');
  
  // 多選狀態管理
  const [selectedAdjustmentIds, setSelectedAdjustmentIds] = useState<string[]>([]);
  const [batchApprovalVisible, setBatchApprovalVisible] = useState(false);
  const [batchApprovalForm] = Form.useForm();


  // 篩選狀態（已提前定義於頂部）

  // 展開收起控制
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 移除模擬庫存數據，統一由後端 API 載入

  // 移除本地相關訂單模擬數據，改為後端加載或保持空

  // 庫存調整原因選項
  const adjustmentReasons = [
    { value: 'cycle_count', label: t('週期盤點調整') },
    { value: 'damage', label: t('貨物損壞') },
    { value: 'expired', label: t('貨物過期') },
    { value: 'lost', label: t('貨物丟失') },
    { value: 'found', label: t('發現額外庫存') },
    { value: 'return', label: t('退貨入庫') },
    { value: 'correction', label: t('系統錯誤修正') },
    { value: 'other', label: t('其他原因') }
  ];

  useEffect(() => {
    loadStock(1, 20);
    loadAdjustments();
  }, [loadStock]);

  // 移除本地 mock 加載函式，改為 loadStock 由後端載入

  const loadAdjustments = async (page = 1, limit = 20, status?: string) => {
    try {
      const resp: any = await apiService.getStockAdjustments({ 
        page, 
        limit, 
        status: (status ?? adjustmentStatus) && (status ?? adjustmentStatus) !== 'all' ? (status ?? adjustmentStatus) as any : undefined,
        sku: (adjustmentSearchSku || '').trim() || undefined
      });
      const list: StockAdjustment[] = (resp?.data || []).map((r: any) => ({
        id: r.id,
        reference: r.reference || '',  // ADJ ID, e.g., ADJ-20251211-062155-527-846
        sku: r.sku,
        productName: r.productName,
        currentQuantity: Number(r.currentQuantity || 0),
        adjustmentQuantity: Number(r.adjustmentQuantity || 0),
        newQuantity: Number(r.newQuantity || 0),
        reason: r.reason || '',
        reasonCode: r.reasonCode || 'adjustment',
        notes: r.notes || '',
        requestedBy: r.requestedBy || t('System'),
        requestedAt: r.requestedAt || '',
        reviewedBy: r.reviewedBy || undefined,
        reviewedAt: r.reviewedAt || undefined,
        status: r.status || 'pending',
        warehouse: r.warehouse || '',
        location: r.location || '',
        urgency: r.urgency || 'normal',
        alternateSkuList: Array.isArray(r.alternateSkuList) ? r.alternateSkuList : [] // 添加備用 SKU 列表
      }));
      setAdjustments(list);
      const total = resp?.pagination?.total ?? list.length;
      setAdjustmentTotal(total);
      setAdjustmentCurrentPage(page);
      setAdjustmentPageSize(limit);
    } catch (error) {
      message.error(t('載入調整申請失敗'));
    }
  };

  // 調整審核查詢條件變更後，防抖刷新
  useEffect(() => {
    const t = setTimeout(() => {
      loadAdjustments(1, adjustmentPageSize);
    }, 300);
    return () => clearTimeout(t);
  }, [adjustmentSearchSku, adjustmentStatus, adjustmentPageSize]);

  const handleViewDetails = async (stock: StockItem) => {
    setDetailsVisible(true);
    setDetailsLoading(true);
    try {
      const current = stockData.find(s => s.id === stock.id) || stock;
      const resp: any = await apiService.getStockItem(stock.id).catch(() => null);
      
      // 获取产品的所有货位信息（详细列表）
      let productLocations: LocationDetail[] = [];
      try {
        const locationsResp: any = await apiService.get(`/locations/products/${encodeURIComponent(stock.sku)}`);
        productLocations = locationsResp?.locations || [];
      } catch (error) {
        console.error('Failed to fetch product locations:', error);
      }

      // Calculate unassigned location quantity (warehouse-level stock without specific location)
      const totalLocationQty = productLocations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);
      const warehouseTotalQty = stock.stockQuantity || 0;
      const shippingQty = stock.outboundQuantity || 0;

      // Unassigned = Total - Location stock - Shipping (shipping stock is already picked from location)
      const unassignedQty = warehouseTotalQty - totalLocationQty - shippingQty;

      // Add "[出庫中]" entry if there's shipping stock
      if (shippingQty > 0) {
        productLocations = [
          {
            orderNumber: '-',
            lotNumber: '-',
            expirationDate: undefined,
            locationPath: '[出庫中]',
            quantity: shippingQty,
            updatedAt: new Date().toISOString()
          },
          ...productLocations
        ];
      }

      // Assign unallocated stock to default location KC/KC instead of showing "未分配庫位"
      if (unassignedQty > 0) {
        productLocations = [
          {
            orderNumber: '-',
            lotNumber: '-',
            expirationDate: undefined,
            locationPath: 'KC/KC',
            quantity: unassignedQty,
            updatedAt: new Date().toISOString()
          },
          ...productLocations
        ];
      }

      // 超售欠貨：以負數列示，使貨位分佈加總 = 可銷售數量（貨位庫存 − 超售欠貨 − 補發中）
      const modalDebtQty = Number((resp && resp.oversellDebt) ?? current.oversellDebt ?? 0);
      if (modalDebtQty > 0) {
        productLocations = [
          ...productLocations,
          {
            orderNumber: '-',
            lotNumber: '-',
            expirationDate: undefined,
            locationPath: `[${t('超售欠貨')}]`,
            quantity: -modalDebtQty,
            updatedAt: new Date().toISOString()
          }
        ];
      }

      // 補發中：補發單已建立但尚未處理，該數量保留給欠貨客戶
      const modalPendReplQty = Number((resp && resp.pendingReplacementQty) ?? current.pendingReplacementQty ?? 0);
      if (modalPendReplQty > 0) {
        productLocations = [
          ...productLocations,
          {
            orderNumber: '-',
            lotNumber: '-',
            expirationDate: undefined,
            locationPath: `[${t('補發中')}]`,
            quantity: -modalPendReplQty,
            updatedAt: new Date().toISOString()
          }
        ];
      }
      
      if (resp) {
        const item: StockItem = {
          id: resp.id || current.id,
          ownerAccount: resp.supplier ?? current.ownerAccount,
          sku: resp.itemCode ?? current.sku,
          barcode: resp.barcode ?? current.barcode,
          productName: resp.itemName ?? current.productName,
          updateTime: resp.updatedAt ?? current.updateTime,
          stockQuantity: Number(resp.total ?? current.stockQuantity ?? 0),
          availableQuantity: Number(resp.available ?? current.availableQuantity ?? 0),
          totalOrderedQuantity: Number(resp.totalOrderedQuantity ?? current.totalOrderedQuantity ?? 0),
          outboundQuantity: Number(resp.shipping ?? current.outboundQuantity ?? 0),
          expectedQuantity: Number(resp.expectedQuantity ?? resp.pendingInQuantity ?? current.expectedQuantity ?? 0),
          occupiedCBM: current.occupiedCBM || 0,
          lastInboundOrder: current.lastInboundOrder || '',
          lastInboundQuantity: current.lastInboundQuantity || 0,
          lastOutboundOrder: current.lastOutboundOrder || '',
          lastOutboundQuantity: current.lastOutboundQuantity || 0,
          warehouse: resp.warehouseName ?? current.warehouse ?? '',
          category: resp.category ?? current.category ?? '',
          unitPrice: current.unitPrice || 0,
          totalValue: current.totalValue || 0,
          reorderLevel: current.reorderLevel || 0,
          supplier: resp.supplier ?? current.supplier ?? '',
          status: Number((resp.total ?? current.stockQuantity) ?? 0) === 0
            ? 'out_of_stock'
            : (Number((resp.available ?? current.availableQuantity) ?? 0) <= 0 ? 'low_stock' : 'normal'),
          alternateSkuList: Array.isArray(resp.alternateSkuList) ? resp.alternateSkuList : [],  // 添加備用 SKU 列表
          oversellDebt: Number(resp.oversellDebt ?? current.oversellDebt ?? 0),
          pendingReplacementQty: Number(resp.pendingReplacementQty ?? current.pendingReplacementQty ?? 0)
        };
        setSelectedStock(item);
        // Reset pagination and fetch related orders with pagination
        setRelatedOrdersPage(1);
        setRelatedOrdersType('all');
        fetchRelatedOrders(item.id, 1, relatedOrdersPageSize, 'all');
        setProductLocations(productLocations);
      } else {
        setSelectedStock({
          ...current,
          alternateSkuList: Array.isArray(current.alternateSkuList) ? current.alternateSkuList : []  // 添加備用 SKU 列表
        });
        // Reset related orders and pagination state
        setRelatedOrders([]);
        setRelatedOrdersTotal(0);
        setRelatedOrdersPage(1);
        setRelatedOrdersType('all');
        setProductLocations(productLocations);
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fetch related orders with pagination
  const fetchRelatedOrders = async (productId: string, page: number = 1, pageSize: number = 20, type: 'all' | 'outbound' | 'inbound' | 'adjustment' = 'all') => {
    setRelatedOrdersLoading(true);
    try {
      const resp: any = await apiService.getRelatedOrders(productId, { page, limit: pageSize, type });
      if (resp && resp.data) {
        setRelatedOrders(resp.data);
        setRelatedOrdersTotal(resp.pagination?.total || 0);
        setRelatedOrdersPage(resp.pagination?.page || 1);
      } else {
        setRelatedOrders([]);
        setRelatedOrdersTotal(0);
      }
    } catch (error) {
      console.error('Error fetching related orders:', error);
      setRelatedOrders([]);
      setRelatedOrdersTotal(0);
    } finally {
      setRelatedOrdersLoading(false);
    }
  };

  // Handle related orders pagination change
  const handleRelatedOrdersPageChange = (page: number, pageSize?: number) => {
    const newPageSize = pageSize || relatedOrdersPageSize;
    setRelatedOrdersPage(page);
    setRelatedOrdersPageSize(newPageSize);
    if (selectedStock?.id) {
      fetchRelatedOrders(selectedStock.id, page, newPageSize, relatedOrdersType);
    }
  };

  // Handle related orders type filter change
  const handleRelatedOrdersTypeChange = (type: 'all' | 'outbound' | 'inbound' | 'adjustment') => {
    setRelatedOrdersType(type);
    setRelatedOrdersPage(1);
    if (selectedStock?.id) {
      fetchRelatedOrders(selectedStock.id, 1, relatedOrdersPageSize, type);
    }
  };

  const handleStockAdjustment = async (stock: StockItem) => {
    setAdjustmentVisible(true);
    setAdjustmentLoading(true);
    setStockLocations([]);
    setLocationAdjustments({});
    try {
      const current = stockData.find(s => s.id === stock.id) || stock;
      const resp: any = await apiService.getStockItem(stock.id).catch(() => null);
      const base = resp ? {
        id: resp.id || current.id,
        sku: resp.itemCode || current.sku,
        productName: resp.itemName || current.productName,
        stockQuantity: Number(resp.total ?? current.stockQuantity ?? 0),
        warehouse: resp.warehouseName || current.warehouse || '',
        alternateSkuList: Array.isArray(resp.alternateSkuList) ? resp.alternateSkuList : []
      } : {
        id: current.id,
        sku: current.sku,
        productName: current.productName,
        stockQuantity: current.stockQuantity,
        warehouse: current.warehouse,
        alternateSkuList: Array.isArray(current.alternateSkuList) ? current.alternateSkuList : []
      };
      setSelectedStock({
        ...stock,
        warehouse: base.warehouse,
        stockQuantity: base.stockQuantity
      });

      // 加載位置庫存信息 (新功能)
      try {
        console.log('[Stock Adjustment] Loading locations for productId:', stock.id);
        const locResp = await apiService.getStockLocations(stock.id);
        console.log('[Stock Adjustment] API Response:', locResp);
        if (locResp.success && locResp.data?.locations) {
          console.log('[Stock Adjustment] Found locations:', locResp.data.locations.length);
          setStockLocations(locResp.data.locations);
        } else {
          console.log('[Stock Adjustment] No locations in response or failed:', locResp);
        }
      } catch (err) {
        console.error('[Stock Adjustment] Failed to load stock locations:', err);
      }

      const cached = lastEdits[base.id];
      adjustmentForm.setFieldsValue({
        sku: base.sku,
        productName: base.productName,
        currentQuantity: cached?.currentQuantity ?? base.stockQuantity,
        adjustmentQuantity: cached?.adjustmentQuantity ?? 0,
        newQuantity: cached?.newQuantity ?? base.stockQuantity,
        reason: cached?.reason ?? '',
        notes: cached?.notes ?? ''
      });
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const handleAdjustmentSubmit = async (values: any) => {
    try {
      setAdjustmentLoading(true);

      // 檢查是否有位置調整 (新功能)
      const hasLocationAdjustments = Object.keys(locationAdjustments).some(
        key => locationAdjustments[key] !== 0
      );

      if (hasLocationAdjustments) {
        // 使用新的位置調整 API
        const adjustments = Object.entries(locationAdjustments)
          .filter(([_, qty]) => qty !== 0)
          .map(([inboundItemId, adjustmentQuantity]) => ({
            inboundItemId,
            adjustmentQuantity
          }));

        if (adjustments.length === 0) {
          message.error(t('請至少對一個位置進行調整'));
          setAdjustmentLoading(false);
          return;
        }

        console.log('提交位置調整數據:', {
          id: selectedStock?.id,
          locationAdjustments: adjustments,
          reasonCode: values.reason,
          notes: values.notes
        });

        const resp = await apiService.submitLocationAdjustment(selectedStock!.id, {
          locationAdjustments: adjustments,
          reasonCode: values.reason,
          notes: values.notes
        });

        if (resp.success) {
          // Only the lines the server actually applied. A line is rejected when
          // it would take its batch below zero, and adding the requested total
          // here would show stock that was never moved.
          const lineResults: any[] = Array.isArray(resp.data?.results) ? resp.data.results : [];
          const rejected = lineResults.filter((r) => r?.success === false);
          const applied = lineResults.filter((r) => r?.success !== false);

          if (lineResults.length > 0 && applied.length === 0) {
            // Nothing moved. Keep the modal open with the entered values so the
            // operator can move the deduction to the batch the stock is really in.
            message.error(t('調整未執行：扣減數量超過批次現有庫存，批次庫存不可低於 0。請確認貨品實際所在的批次。'));
            return;
          }

          // 計算總調整量
          const totalAdj = lineResults.length > 0
            ? applied.reduce((sum, r) => sum + Number(r.adjustmentQuantity || 0), 0)
            : adjustments.reduce((sum, a) => sum + a.adjustmentQuantity, 0);

          // 更新本地庫存顯示
          setStockData(prev => prev.map(item =>
            item.id === selectedStock!.id
              ? {
                  ...item,
                  stockQuantity: (item.stockQuantity || 0) + totalAdj,
                  availableQuantity: Math.max(0, (item.availableQuantity || 0) + totalAdj),
                  updateTime: moment().format('YYYY-MM-DD HH:mm:ss')
                }
              : item
          ));

          if (rejected.length > 0) {
            message.warning(
              t('已調整 {{applied}} 個批次；{{rejected}} 個批次未執行，因扣減會令批次庫存低於 0 (參考編號: {{ref}})', {
                applied: applied.length,
                rejected: rejected.length,
                ref: resp.data.adjustmentReference,
              }),
              8,
            );
          } else {
            message.success(t('庫存調整申請已提交 (參考編號: {{ref}})', { ref: resp.data.adjustmentReference }));
          }
        }
      } else {
        // 舊版：無位置選擇時的調整 (倉庫級別)
        const adjustmentQty = Number(values.adjustmentQuantity);
        if (!Number.isFinite(adjustmentQty) || adjustmentQty === 0) {
          message.error(t('調整數量不能為 0，請輸入正數（增加）或負數（減少）'));
          setAdjustmentLoading(false);
          return;
        }

        console.log('提交調整數據 (舊版):', {
          id: selectedStock?.id,
          adjustmentQuantity: adjustmentQty,
          reasonCode: values.reason,
          notes: values.notes
        });

        const adjustmentData: Partial<StockAdjustment> = {
          sku: selectedStock?.sku,
          productName: selectedStock?.productName,
          currentQuantity: selectedStock?.stockQuantity || 0,
          adjustmentQuantity: adjustmentQty,
          newQuantity: (selectedStock?.stockQuantity || 0) + adjustmentQty,
          reason: adjustmentReasons.find(r => r.value === values.reason)?.label || '',
          reasonCode: values.reason,
          notes: values.notes,
          requestedBy: t('Current User'),
          requestedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          status: 'pending',
          warehouse: selectedStock?.warehouse || '',
          location: '',
          urgency: 'normal'
        };

        await apiService.updateStockItem(selectedStock!.id, {
          adjustmentQuantity: adjustmentQty,
          reasonCode: values.reason,
          notes: values.notes
        }).catch(async () => {
          await new Promise(resolve => setTimeout(resolve, 800));
        });

        setStockData(prev => prev.map(item =>
          item.id === selectedStock!.id
            ? { ...item, stockQuantity: Number((adjustmentData as any).newQuantity || 0), availableQuantity: Math.max(0, (item.availableQuantity || 0) + adjustmentQty), updateTime: moment().format('YYYY-MM-DD HH:mm:ss') }
            : item
        ));

        // 保存本地回顯緩存
        setLastEdits(prev => ({
          ...prev,
          [selectedStock!.id]: {
            adjustmentQuantity: adjustmentQty,
            reason: values.reason,
            notes: values.notes,
            newQuantity: (selectedStock?.stockQuantity || 0) + adjustmentQty,
            currentQuantity: selectedStock?.stockQuantity || 0
          }
        }));

        message.success(t('庫存調整申請已提交，等待審核'));
      }

      // 立即刷新一次調整列表，獲取後端生成的真實調整ID
      await loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);

      setAdjustmentVisible(false);
      adjustmentForm.resetFields();
      setLocationAdjustments({});
    } catch (error) {
      console.error('提交庫存調整失敗:', error);
      message.error(t('提交庫存調整失敗'));
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Stock Adjustment相關處理函數
  const handleViewAdjustmentDetails = (adjustment: StockAdjustment) => {
    setSelectedAdjustment(adjustment);
    setAdjustmentDetailsVisible(true);
  };

  const handleApproval = (adjustment: StockAdjustment, action: 'approve' | 'reject') => {
    setSelectedAdjustment(adjustment);
    approvalForm.setFieldsValue({
      action,
      reason: ''
    });
    setApprovalVisible(true);
  };

  const handleApprovalSubmit = async (values: any) => {
    try {
      // 呼叫後端 API
      await apiService.processStockAdjustment(selectedAdjustment!.id, {
        action: values.action,
        reason: values.reason,
        reviewedBy: 'Current User'
      });

      // 成功後刷新列表（保持當前分頁）
      await loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);

      message.success(values.action === 'approve' ? t('調整申請已批準') : t('調整申請已拒絕'));
      setApprovalVisible(false);
      approvalForm.resetFields();
    } catch (error) {
      message.error(t('處理審核失敗'));
    }
  };

  // 多選處理函數
  const handleBatchSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedAdjustmentIds(selectedRowKeys as string[]);
  };

  const handleBatchApproval = (action: 'approve' | 'reject') => {
    if (selectedAdjustmentIds.length === 0) {
      message.warning(t('請先選擇要處理的調整申請'));
      return;
    }
    
    batchApprovalForm.setFieldsValue({
      action,
      reason: ''
    });
    setBatchApprovalVisible(true);
  };

  const handleBatchApprovalSubmit = async (values: any) => {
    try {
      await apiService.batchProcessStockAdjustments({
        action: values.action,
        ids: selectedAdjustmentIds,
        reason: values.reason,
        reviewedBy: 'Current User'
      });

      await loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);

      message.success(values.action === 'approve' ? t('已批準 {{count}} 個調整申請', { count: selectedAdjustmentIds.length }) : t('已拒絕 {{count}} 個調整申請', { count: selectedAdjustmentIds.length }));
      setBatchApprovalVisible(false);
      setSelectedAdjustmentIds([]);
      batchApprovalForm.resetFields();
    } catch (error) {
      message.error(t('批量處理審核失敗'));
    }
  };



  const handleExportExcel = async () => {
    try {
      const asOfDateStr = asOfDate ? asOfDate.format('YYYY-MM-DD') : undefined;
      // A time only means anything alongside a date.
      const asOfMomentStr =
        asOfDate && asOfTime ? `${asOfDate.format('YYYY-MM-DD')} ${asOfTime.format('HH:mm:ss')}` : undefined;
      message.loading(
        asOfMomentStr
          ? t('正在導出 {{asOfMomentStr}} 的庫存...', { asOfMomentStr })
          : asOfDateStr
            ? t('正在導出 {{asOfDateStr}} 庫存快照...', { asOfDateStr })
            : t('正在導出庫存報表...'),
        0,
      );

      // Call backend to export all stock data with location/lot/expiry
      const resp: any = await apiService.exportStockData({
        search: searchText?.trim() || undefined,
        customerName: selectedCustomerName || undefined,
        storeCode: selectedStoreCode || undefined,
        // asOfMoment wins when present; the backend ignores asOfDate in that case.
        asOfDate: asOfMomentStr ? undefined : asOfDateStr,
        asOfMoment: asOfMomentStr,
        // Only the live export supports this; the snapshot paths ignore it, which
        // is why the checkbox is disabled once a date is picked.
        includeAggregateRemainder: !asOfDateStr && !asOfMomentStr && includeAggregateRemainder,
      });

      if (!resp?.success || !resp?.data) {
        message.destroy();
        // Special-case the snapshot-not-available error so the user knows the earliest date
        if (resp?.error === 'No snapshot available for this date') {
          const earliest = resp?.earliestAvailable || t('無');
          message.error(t('所選日期 ({{date}}) 沒有庫存快照記錄。最早可用日期：{{earliest}}', { date: resp?.date, earliest }));
          return;
        }
        message.error(t('導出失敗: ') + (resp?.message || resp?.error || t('無數據')));
        return;
      }

      const exportData = resp.data;

      // Generate Excel with location, lot, expiry columns
      // New columns are appended rather than inserted, so every existing column
      // keeps its position and anything the client already built on this file
      // (formulas, pivot tables) still points at the right data.
      //
      // 可銷售數量 and 總庫存 are per SKU-and-warehouse, but each row here is one
      // lot/location, so a SKU stored in several places repeats the same figure
      // down its rows. They read correctly per row; summing the column counts a
      // SKU once per location.
      // A moment export carries no lot, location or expiry — the system never
      // recorded which lot an outbound took, so those cannot be recovered for a
      // past instant. The columns are dropped rather than left blank: an empty
      // Lot column reads as "no lot", which would be a different claim.
      // A moment export drops only what genuinely cannot be known for a past
      // instant. Dimensions and CBM describe the product rather than the stock,
      // so they stay. The quantity is the total — the same figure the nightly
      // export calls 總庫存 — and is named that way rather than a bare "Qty".
      // 可銷售數量 is the one quantity that cannot come back: it is the total
      // less what was in the middle of shipping, and that split is not recorded
      // over time. Lot, location and expiry are absent for the reason above.
      const headers = asOfMomentStr
        ? [
            'SKU ID', 'SKU Name', 'Barcode', 'UOM', 'Customer', 'Warehouse',
            '產品長度 (cm)', '產品寬度 (cm)', '產品高度 (cm)', 'CBM 包裝/散件體積 (m³)',
            // Named for what it is rather than what it is made of. The nightly
            // export calls this 總庫存 (可銷售+出庫中) because there it can show
            // both parts; here it cannot, and a name promising a breakdown that
            // is not in the file only invites the question of where it went.
            '總庫存 (含出庫中)'
          ]
        : [
            'SKU ID', 'SKU Name', 'Barcode', 'Lot', 'Location', 'Expiry Date', 'UOM', 'Qty', 'Customer', 'Warehouse',
            '產品長度 (cm)', '產品寬度 (cm)', '產品高度 (cm)', 'CBM 包裝/散件體積 (m³)',
            '可銷售數量', '總庫存 (可銷售+出庫中)'
          ];

      // Blank, not 0, when a figure is genuinely absent — an unmeasured product
      // must not appear to be 0 cm long.
      const num = (v: any) => (v === null || v === undefined || v === '' ? '' : Number(v));

      const rows = exportData.map((item: any) =>
        asOfMomentStr
          ? [
              item.sku || '',
              item.skuName || '',
              item.barcode || '',
              item.uom || 'PCS',
              item.customerName || '',
              item.warehouseName || '',
              num(item.productLength),
              num(item.productWidth),
              num(item.productHeight),
              num(item.cbm),
              item.qty || 0
            ]
          : [
              item.sku || '',
              item.skuName || '',
              item.barcode || '',  // Excel handles comma-separated values in cells properly
              item.lot || '',
              item.location || '',
              item.expiryDate || '',
              item.uom || 'PCS',
              item.qty || 0,
              item.customerName || '',
              item.warehouseName || '',
              num(item.productLength),
              num(item.productWidth),
              num(item.productHeight),
              num(item.cbm),
              num(item.availableQty),
              num(item.totalStock)
            ]
      );

      // Create worksheet with headers and data. A moment export gets a line
      // above the table saying which instant it is, because not knowing that was
      // the original complaint.
      // A reconstructed date cannot say how much was sellable, because the
      // split between sellable and in-transit was never stored for that day.
      // The column comes back empty, so the file states the reason rather than
      // leaving a reader to guess whether empty means zero.
      const reconstructed = resp.source && resp.source !== 'live';
      const wsData = asOfMomentStr
        ? [[`庫存時刻 As at ${asOfMomentStr} (HKT) — 按 SKU 匯總，不含批號/庫位/到期日及可銷售數量 / per SKU; no lot, location, expiry or sellable split`], [], headers, ...rows]
        : reconstructed
          ? [[`${asOfDateStr} — 此日期為推算重建 (${resp.source})，可銷售數量無法得知，該欄留空 / reconstructed date; sellable quantity unknown and left blank`], [], headers, ...rows]
          : [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      ws['!cols'] = [
        { wch: 35 },  // SKU ID
        { wch: 50 },  // SKU Name
        { wch: 30 },  // Barcode
        { wch: 20 },  // Lot
        { wch: 25 },  // Location
        { wch: 12 },  // Expiry Date
        { wch: 8 },   // UOM
        { wch: 10 },  // Qty
        { wch: 30 },  // Customer
        { wch: 15 },  // Warehouse
        { wch: 14 },  // 產品長度 (cm)
        { wch: 14 },  // 產品寬度 (cm)
        { wch: 14 },  // 產品高度 (cm)
        { wch: 24 },  // CBM 包裝/散件體積 (m³)
        { wch: 14 },  // 可銷售數量
        { wch: 22 },  // 總庫存 (可銷售+出庫中)
      ];

      // Create workbook and add the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Stock Report');

      // Generate Excel file and download
      const sourceSuffix = resp.source && String(resp.source).startsWith('aggregate-only') ? '_aggregate' : '';
      const filenameDate = asOfMomentStr
        ? `asat_${asOfMomentStr.replace(/[-: ]/g, '')}`
        : asOfDateStr
          ? `asof_${asOfDateStr.replace(/-/g, '')}${sourceSuffix}`
          : moment().format('YYYYMMDD_HHmmss');
      XLSX.writeFile(wb, `stock_report_${filenameDate}.xlsx`);

      message.destroy();
      const sourceLabel = resp.source && resp.source !== 'live' ? t(' — 來源: {{source}}', { source: resp.source }) : '';
      message.success(
        `${t('庫存報表已導出 ({{count}} 條記錄)', { count: exportData.length })}${
          asOfMomentStr
            ? t(' — {{asOfMomentStr}} 的庫存（按 SKU 匯總，不含批號/庫位/到期日及可銷售數量）', { asOfMomentStr })
            : asOfDateStr
              ? t(' — 快照日期 {{asOfDateStr}}', { asOfDateStr })
              : ''
        }${asOfMomentStr ? '' : sourceLabel}`,
        asOfMomentStr ? 8 : undefined,
      );
    } catch (error) {
      message.destroy();
      console.error('導出失敗:', error);
      message.error(t('導出失敗: ') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 一鍵清零 - 處理批量選擇變更
  const handleStockSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedStockIds(selectedRowKeys);
  };

  // 一鍵清零 - 打開確認彈窗
  const handleOpenResetModal = () => {
    if (selectedStockIds.length === 0) {
      message.warning(t('請先選擇要清零的庫存項'));
      return;
    }
    setResetModalVisible(true);
  };

  // 一鍵清零 - 執行批量重置
  const handleBulkResetInventory = async () => {
    if (selectedStockIds.length === 0) {
      message.warning(t('請先選擇要清零的庫存項'));
      return;
    }

    // Get SKUs from selected stock items
    const selectedSkus = stockData
      .filter(item => selectedStockIds.includes(item.id))
      .map(item => item.sku);

    if (selectedSkus.length === 0) {
      message.error(t('無法獲取所選項目的SKU'));
      return;
    }

    try {
      setResetLoading(true);
      const response = await apiService.bulkResetInventory(selectedSkus);

      if (response.success) {
        message.success(t('成功清零 {{count}} 個SKU的庫存', { count: response.data?.skusProcessed || selectedSkus.length }));
        setResetModalVisible(false);
        setSelectedStockIds([]);
        // Refresh the stock list
        loadStock(stockCurrentPage, stockPageSize);
      } else {
        message.error(response.error || t('清零失敗'));
      }
    } catch (error) {
      console.error('批量清零失敗:', error);
      message.error(t('批量清零失敗: ') + (error instanceof Error ? error.message : t('未知錯誤')));
    } finally {
      setResetLoading(false);
    }
  };

  // Fetches ALL pages of related orders (not just the current view).
  // Backend caps `limit` at 100, so we walk pages until total reached, with a 50k safety cap.
  const handleExportRelatedOrders = async () => {
    if (!selectedStock) {
      message.warning(t('沒有相關訂單可導出'));
      return;
    }

    const BATCH_SIZE = 100;
    const MAX_ROWS = 50000;
    message.loading({ content: t('正在導出全部相關訂單...'), key: 'export-related', duration: 0 });

    try {
      const all: RelatedOrder[] = [];
      let page = 1;
      let total = Infinity;
      while (all.length < total && all.length < MAX_ROWS) {
        const resp: any = await apiService.getRelatedOrders(selectedStock.id, {
          page,
          limit: BATCH_SIZE,
          type: relatedOrdersType,
        });
        // Guard only on the data shape — main didn't previously set success:true on
        // getRelatedOrders so this check was firing on every request and producing the
        // '導出失敗: 導出失敗' double-prefixed toast. Backend now also returns success,
        // but dropping the `!resp?.success` check makes the export resilient to future
        // response-shape changes on either side.
        if (!Array.isArray(resp?.data)) {
          throw new Error(resp?.message || resp?.error || t('無法讀取相關訂單'));
        }
        all.push(...resp.data);
        total = Number(resp.pagination?.total ?? all.length);
        if (resp.data.length < BATCH_SIZE) break;
        page += 1;
      }

      if (all.length === 0) {
        message.destroy('export-related');
        message.warning(t('沒有相關訂單可導出'));
        return;
      }

      const csvContent = generateRelatedOrdersCSVContent(all);
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `related_orders_${selectedStock?.sku}_${moment().format('YYYYMMDD_HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.destroy('export-related');
      message.success(t('相關訂單已導出 ({{count}} 條記錄)', { count: all.length }));
    } catch (error) {
      message.destroy('export-related');
      console.error('導出相關訂單失敗:', error);
      message.error(t('導出失敗: ') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  const generateRelatedOrdersCSVContent = (data: RelatedOrder[]) => {
    const headers = [t('訂單號'), t('類型'), t('日期'), t('數量'), t('狀態')];

    const typeMap = {
      inbound: t('預定入庫'),
      outbound: t('出庫'),
      transfer: t('收貨')
    };

    const rows = data.map(item => [
      item.orderNumber,
      typeMap[item.type as keyof typeof typeMap] || item.type,
      item.date,
      item.quantity,
      item.status
    ]);

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  };

  const handleExportProductMovementSummary = async () => {
    try {
      // 顯示日期範圍選擇彈窗
      setReportModalVisible(true);
    } catch (error) {
      console.error('生成報告失敗:', error);
      message.error(t('生成報告失敗: ') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 處理報告生成
  const handleGenerateReport = async (values: any) => {
    try {
      const { dateRange, dateType, onlyInOut } = values;
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      message.loading(t('正在生成報告，請稍候...'), 0);
      setReportModalVisible(false);

      // 調用API生成報告（傳遞當前客戶和Store Code篩選條件）
      const { blob, fileName } = await apiService.generateProductMovementSummary({
        dateStart: startDate,
        dateEnd: endDate,
        dateType: dateType || 'created',
        customerName: selectedCustomerName || undefined,
        storeCode: selectedStoreCode || undefined,
        // Unticked sends nothing, so the report is byte-identical to before.
        onlyInOut: !!onlyInOut
      });

      // 下載文件
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);

      message.destroy();
      message.success(t('Product movement summary by customer 報告已導出'));
    } catch (error) {
      message.destroy();
      console.error('生成報告失敗:', error);
      message.error(t('生成報告失敗: ') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 篩選數據
  const filteredData = stockData.filter(item => {
    const matchesSearch = !searchText || 
      item.sku.toLowerCase().includes(searchText.toLowerCase()) ||
      item.productName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.barcode.includes(searchText) ||
      (item.alternateSkuList && item.alternateSkuList.some(sku => sku.toLowerCase().includes(searchText.toLowerCase())));
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  // 定義所有可用的庫存列
  const allStockColumns = {
    sku: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('sku')}
        >
          SKU
          {getStockSortIcon('sku')}
        </div>
      ),
      dataIndex: 'sku',
      key: 'sku',
      width: 220,
      fixed: 'left' as 'left',
      sorter: true,
      render: (text: string) => <Text code strong>{text}</Text>
    },
    productName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('productName')}
        >
          {t('產品名稱')}
                          {getStockSortIcon('productName')}
        </div>
      ),
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      ellipsis: { showTitle: false },
      sorter: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      )
    },
    barcode: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('barcode')}
        >
          {t('條碼')}
                          {getStockSortIcon('barcode')}
        </div>
      ),
      dataIndex: 'barcode',
      key: 'barcode',
      width: 140,
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },
    stockQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('stockQuantity')}
        >
          {t('庫存數量')}
                          {getStockSortIcon('stockQuantity')}
        </div>
      ),
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number, record: StockItem) => (
        <div>
          <Text strong style={{
            color: value < 0 ? '#cf1322' :
                   record.status === 'low_stock' ? '#faad14' :
                   record.status === 'out_of_stock' ? '#ff4d4f' : '#52c41a'
          }}>
            {value}
          </Text>
        </div>
      )
    },
    availableQuantity: {
      title: (
        <div
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('availableQuantity')}
        >
          {t('可銷售數量')}
                          {getStockSortIcon('availableQuantity')}
        </div>
      ),
      dataIndex: 'availableQuantity',
      key: 'availableQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number, record: StockItem) => (
        <Space size={4}>
          <Text style={{
            backgroundColor: value < 0 ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
            color: value < 0 ? '#cf1322' : '#389e0d',
            padding: '2px 6px',
            borderRadius: 4,
            display: 'inline-block',
            minWidth: 32,
            textAlign: 'right'
          }}>
            {value}
          </Text>
          {(record.oversellDebt || 0) > 0 && (
            <Tooltip title={t('已確認超售 {{qty}} 件，等待補貨/跟進', { qty: record.oversellDebt })}>
              <Tag color="error" style={{ marginInlineEnd: 0 }}>{t('超售')}</Tag>
            </Tooltip>
          )}
          {(record.pendingReplacementQty || 0) > 0 && (
            <Tooltip title={t('補發單已建立 {{qty}} 件（待處理），該數量保留給欠貨客戶，不可銷售', { qty: record.pendingReplacementQty })}>
              <Tag color="processing" style={{ marginInlineEnd: 0 }}>{t('補發中')}</Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    totalOrderedQuantity: {
      title: (
        <div
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('totalOrderedQuantity')}
        >
          {t('總下單數量')}
                          {getStockSortIcon('totalOrderedQuantity')}
        </div>
      ),
      dataIndex: 'totalOrderedQuantity',
      key: 'totalOrderedQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number = 0, record: StockItem) => {
        const hasWarning = value > (record.availableQuantity || 0);
        return (
          <Tooltip title={hasWarning ? t('警告：總下單數量({{ordered}})超過可銷售數量({{available}})', { ordered: value, available: record.availableQuantity }) : t('PENDING訂單總量: {{value}}', { value })}>
            <Text style={{
              backgroundColor: hasWarning ? 'rgba(255, 77, 79, 0.15)' : 'rgba(24, 144, 255, 0.15)',
              color: hasWarning ? '#cf1322' : '#0958d9',
              padding: '2px 6px',
              borderRadius: 4,
              display: 'inline-block',
              minWidth: 32,
              textAlign: 'right',
              fontWeight: hasWarning ? 'bold' : 'normal'
            }}>
              {value}
            </Text>
          </Tooltip>
        );
      }
    },
    outboundQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('outboundQuantity')}
        >
          {t('出庫中數量')}
                          {getStockSortIcon('outboundQuantity')}
        </div>
      ),
      dataIndex: 'outboundQuantity',
      key: 'outboundQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => value > 0 ? <Badge count={value} color="orange" /> : <Text>0</Text>
    },
    /*expectedQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('expectedQuantity')}
        >
          預期數量
          {getStockSortIcon('expectedQuantity')}
        </div>
      ),
      dataIndex: 'expectedQuantity',
      key: 'expectedQuantity',
      width: 110,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => (
        <Text style={{
          backgroundColor: value > 0 ? 'rgba(24, 144, 255, 0.15)' : 'rgba(0, 0, 0, 0.04)',
          color: value > 0 ? '#0958d9' : '#999',
          padding: '2px 6px',
          borderRadius: 4,
          display: 'inline-block',
          minWidth: 32,
          textAlign: 'right'
        }}>
          {value}
        </Text>
      )
    },*/
    /*occupiedCBM: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('occupiedCBM')}
        >
          佔用CBM
          {getStockSortIcon('occupiedCBM')}
        </div>
      ),
      dataIndex: 'occupiedCBM',
      key: 'occupiedCBM',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => <Text>{value.toFixed(2)}</Text>
    },
    lastInboundOrder: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('lastInboundOrder')}
        >
          最後入庫單
          {getStockSortIcon('lastInboundOrder')}
        </div>
      ),
      dataIndex: 'lastInboundOrder',
      key: 'lastInboundOrder',
      width: 140,
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },
    lastOutboundOrder: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('lastOutboundOrder')}
        >
          最後發貨單
          {getStockSortIcon('lastOutboundOrder')}
        </div>
      ),
      dataIndex: 'lastOutboundOrder',
      key: 'lastOutboundOrder',
      width: 140,
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },*/
    updateTime: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('updateTime')}
        >
          {t('更新時間')}
                          {getStockSortIcon('updateTime')}
        </div>
      ),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 150,
      sorter: true,
      render: (text: string) => <Text type="secondary">{text}</Text>
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleStockColumnSort('status')}
        >
          {t('狀態')}
                          {getStockSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => {
        const statusConfig = {
          normal: { color: 'green', text: t('正常') },
          low_stock: { color: 'orange', text: t('庫存不足') },
          out_of_stock: { color: 'red', text: t('缺貨') },
          excess: { color: 'blue', text: t('庫存過多') }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 220,
      fixed: 'right' as 'right',
      render: (__unused: unknown, record: StockItem) => (
        <Space>
          <Tooltip title={t('查看詳情')}>
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            >
              {t('詳情')}
                                  </Button>
          </Tooltip>
          {!isCustomerRole && (
            <Tooltip title={t('編輯')}>
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleStockAdjustment(record)}
              >
                {t('編輯')}
                                        </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  };

  // 根據列配置生成顯示的列
  const columns: ColumnsType<StockItem> = Object.entries(stockColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allStockColumns[key as keyof typeof allStockColumns];
      if (!column) {
        console.warn(`Missing stock column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 使庫存表支持拖拽列排序（排除 操作 列）
  const { columns: draggableStockColumns, order: stockOrder } = useDraggableColumns<StockItem>({
    columns: columns as any,
    disableDragKeys: ['actions'],
    storageKey: 'inventory-stock-table',
  });

  // 拖拽後與原有列配置 order 同步，確保持久化一致
  useEffect(() => {
    if (!stockOrder || stockOrder.length === 0) return;
    setStockColumnSettings((prev: StockColumnSettings) => {
      const next: StockColumnSettings = { ...prev } as StockColumnSettings;
      stockOrder.forEach((k, idx) => {
        if ((next as any)[k]) (next as any)[k] = { ...(next as any)[k], order: idx };
      });
      (Object.keys(next) as Array<keyof StockColumnSettings>).forEach((k) => {
        if (!stockOrder.includes(k)) {
          const o = (next as any)[k]?.order ?? 9999;
          (next as any)[k] = { ...(next as any)[k], order: Math.max(stockOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('stock-management-columns-config', JSON.stringify(next));
        localStorage.setItem('stock-management-columns-version', '2024-01-v2');
      } catch {}
      return next;
    });
  }, [stockOrder]);

  // 處理庫存列可見性切換
  const handleStockColumnVisibilityChange = (columnKey: StockColumnKey, visible: boolean) => {
    const newSettings: StockColumnSettings = {
      ...(stockColumnSettings as StockColumnSettings),
      [columnKey]: { ...(stockColumnSettings as any)[columnKey], visible }
    } as StockColumnSettings;
    setStockColumnSettings(newSettings);
    localStorage.setItem('stock-management-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('stock-management-columns-version', '2024-01-v3');
  };

  // 顯示所有庫存列
  const showAllStockColumns = () => {
    const allVisibleSettings = {
      sku: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      barcode: { visible: true, order: 2 },
      stockQuantity: { visible: true, order: 3 },
      availableQuantity: { visible: true, order: 4 },
      totalOrderedQuantity: { visible: true, order: 5 },
      outboundQuantity: { visible: true, order: 6 },
      expectedQuantity: { visible: true, order: 7 },
      occupiedCBM: { visible: true, order: 8 },
      lastInboundOrder: { visible: true, order: 9 },
      lastOutboundOrder: { visible: true, order: 10 },
      updateTime: { visible: true, order: 11 },
      status: { visible: true, order: 12 },
      actions: { visible: true, order: 13 }
    };
    setStockColumnSettings(allVisibleSettings);
    localStorage.setItem('stock-management-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('stock-management-columns-version', '2024-01-v6');
    message.success(t('所有列已顯示'));
  };

  // 重置庫存列配置
  const resetStockColumnSettings = () => {
    const defaultSettings = {
      sku: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      barcode: { visible: true, order: 2 },
      stockQuantity: { visible: false, order: 3 },
      availableQuantity: { visible: true, order: 4 },
      totalOrderedQuantity: { visible: true, order: 5 },
      outboundQuantity: { visible: true, order: 6 },
      expectedQuantity: { visible: true, order: 7 },
      occupiedCBM: { visible: false, order: 8 },
      lastInboundOrder: { visible: false, order: 9 },
      lastOutboundOrder: { visible: false, order: 10 },
      updateTime: { visible: true, order: 11 },
      status: { visible: true, order: 12 },
      actions: { visible: true, order: 13 }
    };
    setStockColumnSettings(defaultSettings);
    localStorage.setItem('stock-management-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('stock-management-columns-version', '2024-01-v6');
    message.success(t('列配置已重置'));
  };

  // 庫存列設置菜單
  const stockColumnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allStockColumns).map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleStockColumnVisibilityChange(key as StockColumnKey, !((stockColumnSettings as any)[key]?.visible ?? true));
            }}
          >
            <Checkbox
              checked={(stockColumnSettings as any)[key]?.visible ?? true}
              onChange={(e) => {
                e.stopPropagation();
                handleStockColumnVisibilityChange(key as StockColumnKey, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title :
               key === 'sku' ? 'SKU' :
               key === 'productName' ? t('產品名稱') :
               key === 'barcode' ? t('條碼') :
               key === 'stockQuantity' ? t('庫存數量') :
               key === 'availableQuantity' ? t('可銷售數量') :
               key === 'totalOrderedQuantity' ? t('總下單數量') :
               key === 'outboundQuantity' ? t('出庫中數量') :
               /*key === 'occupiedCBM' ? '佔用CBM' :
               key === 'lastInboundOrder' ? '最後入庫單' :
               key === 'lastOutboundOrder' ? '最後發貨單' :*/
               key === 'updateTime' ? t('更新時間') :
               key === 'status' ? t('狀態') :
               key === 'actions' ? t('操作') : key}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 0' }}>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            showAllStockColumns();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t('顯示所有列')}
                                </Button>
        </div>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            resetStockColumnSettings();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t('重置列設置')}
                                </Button>
        </div>
      </div>
    </div>
  );

  // 處理庫存數據排序
  const sortedAndFilteredStockData = useMemo(() => {
    let result = [...filteredData];
    
    if (stockSortField && stockSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[stockSortField as keyof StockItem];
        let bValue: any = b[stockSortField as keyof StockItem];
        
        // 處理數字字段排序
        if (['stockQuantity', 'availableQuantity', 'outboundQuantity', 'occupiedCBM'].includes(stockSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return stockSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return stockSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filteredData, stockSortField, stockSortOrder]);

  // 相關訂單表格列
  const orderColumns: ColumnsType<RelatedOrder> = [
    {
      title: t('訂單號'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string) => <Text code>{text}</Text>
    },
    {
      title: t('類型'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeConfig: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
          inbound: { color: 'green', text: t('預定入庫'), icon: <InboxOutlined /> },
          outbound: { color: 'blue', text: t('出庫'), icon: <SendOutlined /> },
          transfer: { color: 'orange', text: t('收貨'), icon: <BarChartOutlined /> },
          oversell: { color: 'red', text: t('超售欠貨'), icon: null },
          adjustment: { color: 'orange', text: t('調整'), icon: null }
        };
        const config = typeConfig[type] || { color: 'default', text: type || t('未知'), icon: null };
        return (
          <Space>
            {config.icon}
            <Tag color={config.color}>{config.text}</Tag>
          </Space>
        );
      }
    },
    {
      title: t('日期'),
      dataIndex: 'date',
      key: 'date'
    },
    {
      title: t('數量'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right'
    },
    {
      title: t('狀態'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag>{statusLabel(status)}</Tag>
    },
    {
      title: t('貨位'),
      dataIndex: 'location',
      key: 'location',
      render: (text: string) => text ? <Text type="secondary" style={{ fontSize: '12px' }}>{shortLocation(text)}</Text> : '-'
    },
    {
      title: t('批次號'),
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (text: string) => text ? <Text code style={{ fontSize: '12px' }}>{text}</Text> : '-'
    },
    {
      title: t('到期日'),
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (text: string) => text ? <Text style={{ fontSize: '12px' }}>{text}</Text> : '-'
    },
    {
      title: t('操作'),
      key: 'debtAction',
      width: 110,
      render: (_: any, record: any) => {
        if (record.type !== 'oversell') return null;
        return (
          <Button
            size="small"
            danger
            onClick={() => {
              Modal.confirm({
                title: t('建立補發單'),
                content: t('將為 {{orderNumber}} 的超售欠貨 {{qty}} 件建立補發出庫單，該數量將恢復為可銷售並隨補發單流程重新扣減。確認建立？', { orderNumber: record.orderNumber, qty: Math.abs(Number(record.quantity) || 0) }),
                okText: t('建立補發單'),
                cancelText: t('取消'),
                onOk: async () => {
                  try {
                    const resp: any = await apiService.resolveOversellDebt(record.id);
                    if (resp?.success) {
                      message.success(t('已建立補發單 {{orderNumber}}，欠貨已解除', { orderNumber: resp.data?.orderNumber || '' }));
                      if (selectedStock) {
                        handleViewDetails(selectedStock);
                      }
                      loadStock();
                    } else {
                      message.error(resp?.error || t('建立補發單失敗'));
                    }
                  } catch (e: any) {
                    message.error(e?.message || t('建立補發單失敗'));
                  }
                }
              });
            }}
          >
            {t('建立補發單')}
                          </Button>
        );
      }
    }
  ];

  // 定義所有可用的調整申請列
  const allAdjustmentColumns = {
    reference: {
      title: (
        <div
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reference')}
        >
          {t('調整單號')}
                          {getAdjustmentSortIcon('reference')}
        </div>
      ),
      dataIndex: 'reference',
      key: 'reference',
      width: 200,
      fixed: 'left' as 'left',
      sorter: true,
      render: (text: string) => <Text code>{text}</Text>
    },
    sku: {
      title: (
        <div
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('sku')}
        >
          SKU
          {getAdjustmentSortIcon('sku')}
        </div>
      ),
      dataIndex: 'sku',
      key: 'sku',
      width: 220,
      sorter: true,
      render: (text: string) => <Text code strong>{text}</Text>
    },
    productName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('productName')}
        >
          {t('產品名稱')}
                          {getAdjustmentSortIcon('productName')}
        </div>
      ),
      dataIndex: 'productName',
      key: 'productName',
      width: 180,
      ellipsis: { showTitle: false },
      sorter: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      )
    },
    currentQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('currentQuantity')}
        >
          {t('當前庫存')}
                          {getAdjustmentSortIcon('currentQuantity')}
        </div>
      ),
      dataIndex: 'currentQuantity',
      key: 'currentQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => <Text strong>{value}</Text>
    },
    adjustmentQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('adjustmentQuantity')}
        >
          {t('調整數量')}
                          {getAdjustmentSortIcon('adjustmentQuantity')}
        </div>
      ),
      dataIndex: 'adjustmentQuantity',
      key: 'adjustmentQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => (
        <Text strong style={{ color: value > 0 ? '#52c41a' : '#ff4d4f' }}>
          {value > 0 ? '+' : ''}{value}
        </Text>
      )
    },
    newQuantity: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('newQuantity')}
        >
          {t('調整後')}
                          {getAdjustmentSortIcon('newQuantity')}
        </div>
      ),
      dataIndex: 'newQuantity',
      key: 'newQuantity',
      width: 100,
      align: 'right' as 'right',
      sorter: true,
      render: (value: number) => <Text>{value}</Text>
    },
    reason: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reason')}
        >
          {t('調整原因')}
                          {getAdjustmentSortIcon('reason')}
        </div>
      ),
      dataIndex: 'reason',
      key: 'reason',
      width: 120,
      sorter: true,
      render: (text: string) => <Tag>{text}</Tag>
    },
    notes: {
      title: t('備註'),
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      ellipsis: { showTitle: false },
      render: (text: string) => text ? (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ) : '-'
    },
    requestedBy: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('requestedBy')}
        >
          {t('申請人')}
                          {getAdjustmentSortIcon('requestedBy')}
        </div>
      ),
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      width: 100,
      sorter: true,
      render: (text: string) => (
        <Space>
          <UserOutlined />
          <Text>{text}</Text>
        </Space>
      )
    },
    requestedAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('requestedAt')}
        >
          {t('申請時間')}
                          {getAdjustmentSortIcon('requestedAt')}
        </div>
      ),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 150,
      sorter: true,
      render: (text: string) => (
        <Space>
          <CalendarOutlined />
          <Text type="secondary">{text}</Text>
        </Space>
      )
    },
    reviewedBy: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reviewedBy')}
        >
          {t('審核人')}
                          {getAdjustmentSortIcon('reviewedBy')}
        </div>
      ),
      dataIndex: 'reviewedBy',
      key: 'reviewedBy',
      width: 100,
      sorter: true,
      render: (text: string) => text ? (
        <Space>
          <UserOutlined />
          <Text>{text}</Text>
        </Space>
      ) : '-'
    },
    reviewedAt: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('reviewedAt')}
        >
          {t('審核時間')}
                          {getAdjustmentSortIcon('reviewedAt')}
        </div>
      ),
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      width: 150,
      sorter: true,
      render: (text: string) => text ? (
        <Space>
          <CalendarOutlined />
          <Text type="secondary">{text}</Text>
        </Space>
      ) : '-'
    },
    urgency: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('urgency')}
        >
          {t('緊急度')}
                          {getAdjustmentSortIcon('urgency')}
        </div>
      ),
      dataIndex: 'urgency',
      key: 'urgency',
      width: 100,
      sorter: true,
      render: (urgency: string) => {
        const urgencyConfig = {
          low: { color: 'green', text: t('低') },
          normal: { color: 'blue', text: t('普通') },
          high: { color: 'orange', text: t('高') },
          critical: { color: 'red', text: t('緊急') }
        };
        const config = urgencyConfig[urgency as keyof typeof urgencyConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    status: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleAdjustmentColumnSort('status')}
        >
          {t('狀態')}
                          {getAdjustmentSortIcon('status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => {
        const statusConfig = {
          pending: { color: 'orange', text: t('待審核'), icon: <ClockCircleOutlined /> },
          approved: { color: 'green', text: t('已批準'), icon: <CheckCircleOutlined /> },
          rejected: { color: 'red', text: t('已拒絕'), icon: <CloseCircleOutlined /> }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <Space>
            {config.icon}
            <Tag color={config.color}>{config.text}</Tag>
          </Space>
        );
      }
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: 280,
      fixed: 'right' as 'right',
      render: (__unused: unknown, record: StockAdjustment) => (
        <Space>
          <Tooltip title={t('查看詳情')}>
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewAdjustmentDetails(record)}
            >
              {t('詳情')}
                                  </Button>
          </Tooltip>
          {!isCustomerRole && record.status === 'pending' && (
            <>
              <Popconfirm
                title={t('確認批準此調整申請？')}
                onConfirm={() => handleApproval(record, 'approve')}
                okText={t('確認')}
                cancelText={t('取消')}
              >
                <Tooltip title={t('批準')}>
                  <Button
                    type="default"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={{ color: '#52c41a' }}
                  >
                    {t('批準')}
                                                    </Button>
                </Tooltip>
              </Popconfirm>
              <Tooltip title={t('拒絕')}>
                <Button
                  type="default"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  style={{ color: '#ff4d4f' }}
                  onClick={() => handleApproval(record, 'reject')}
                >
                  {t('拒絕')}
                                              </Button>
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  };

  // 根據列配置生成顯示的調整申請列
  const adjustmentColumns: ColumnsType<StockAdjustment> = Object.entries(adjustmentColumnSettings)
    .filter(([_, config]: [string, any]) => config.visible)
    .sort(([, a]: [string, any], [, b]: [string, any]) => a.order - b.order)
    .map(([key]) => {
      const column = allAdjustmentColumns[key as keyof typeof allAdjustmentColumns];
      if (!column) {
        console.warn(`Missing adjustment column definition for key: ${key}`);
      }
      return column;
    })
    .filter(Boolean); // 過濾掉undefined的列

  // 使調整表支持拖拽列排序（排除 操作 列）
  const { columns: draggableAdjustmentColumns, order: adjustmentOrder } = useDraggableColumns<StockAdjustment>({
    columns: adjustmentColumns as any,
    disableDragKeys: ['actions'],
    storageKey: 'inventory-adjustment-table',
  });

  // 拖拽後同步回列配置
  useEffect(() => {
    if (!adjustmentOrder || adjustmentOrder.length === 0) return;
    setAdjustmentColumnSettings((prev: AdjustmentColumnSettings) => {
      const next: AdjustmentColumnSettings = { ...prev } as AdjustmentColumnSettings;
      adjustmentOrder.forEach((k, idx) => {
        if ((next as any)[k]) (next as any)[k] = { ...(next as any)[k], order: idx };
      });
      (Object.keys(next) as Array<keyof AdjustmentColumnSettings>).forEach((k) => {
        if (!adjustmentOrder.includes(k)) {
          const o = (next as any)[k]?.order ?? 9999;
          (next as any)[k] = { ...(next as any)[k], order: Math.max(adjustmentOrder.length, o) };
        }
      });
      try {
        localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(next));
        localStorage.setItem('adjustment-audit-columns-version', '2024-01-v2');
      } catch {}
      return next;
    });
  }, [adjustmentOrder]);

  // 處理調整申請列可見性切換
  const handleAdjustmentColumnVisibilityChange = (columnKey: AdjustmentColumnKey, visible: boolean) => {
    const newSettings: AdjustmentColumnSettings = {
      ...(adjustmentColumnSettings as AdjustmentColumnSettings),
      [columnKey]: { ...(adjustmentColumnSettings as any)[columnKey], visible }
    } as AdjustmentColumnSettings;
    setAdjustmentColumnSettings(newSettings);
    localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('adjustment-audit-columns-version', '2026-02-v7');
  };

  // 顯示所有調整申請列
  const showAllAdjustmentColumns = () => {
    const allVisibleSettings = {
      reference: { visible: true, order: 0 },
      sku: { visible: true, order: 1 },
      productName: { visible: true, order: 2 },
      currentQuantity: { visible: true, order: 3 },
      adjustmentQuantity: { visible: true, order: 4 },
      newQuantity: { visible: true, order: 5 },
      reason: { visible: true, order: 6 },
      notes: { visible: true, order: 7 },
      requestedBy: { visible: true, order: 8 },
      requestedAt: { visible: true, order: 9 },
      reviewedBy: { visible: true, order: 10 },
      reviewedAt: { visible: true, order: 11 },
      urgency: { visible: true, order: 12 },
      status: { visible: true, order: 13 },
      actions: { visible: true, order: 14 }
    };
    setAdjustmentColumnSettings(allVisibleSettings);
    localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('adjustment-audit-columns-version', '2026-02-v7');
    message.success(t('所有列已顯示'));
  };

  // 重置調整申請列配置
  const resetAdjustmentColumnSettings = () => {
    const defaultSettings = {
      reference: { visible: true, order: 0 },
      sku: { visible: true, order: 1 },
      productName: { visible: true, order: 2 },
      currentQuantity: { visible: true, order: 3 },
      adjustmentQuantity: { visible: true, order: 4 },
      newQuantity: { visible: true, order: 5 },
      reason: { visible: true, order: 6 },
      notes: { visible: false, order: 7 },
      requestedBy: { visible: true, order: 8 },
      requestedAt: { visible: true, order: 9 },
      reviewedBy: { visible: false, order: 10 },
      reviewedAt: { visible: false, order: 11 },
      urgency: { visible: true, order: 12 },
      status: { visible: true, order: 13 },
      actions: { visible: true, order: 14 }
    };
    setAdjustmentColumnSettings(defaultSettings);
    localStorage.setItem('adjustment-audit-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('adjustment-audit-columns-version', '2026-02-v7');
    message.success(t('列配置已重置'));
  };

  // 調整申請列設置菜單
  const adjustmentColumnSettingsMenu = (
    <Menu>
      <Menu.Item key="header" disabled>
        <Text strong>{t('列設置')}</Text>
      </Menu.Item>
      <Menu.Divider />
      {Object.entries(allAdjustmentColumns).map(([key, column]) => (
        <Menu.Item key={key}>
          <Checkbox
            checked={(adjustmentColumnSettings as any)[key]?.visible ?? true}
            onChange={(e) => handleAdjustmentColumnVisibilityChange(key as AdjustmentColumnKey, e.target.checked)}
          >
            {typeof column.title === 'string' ? column.title :
             key === 'reference' ? t('調整單號') :
             key === 'sku' ? 'SKU' :
             key === 'productName' ? t('產品名稱') :
             key === 'currentQuantity' ? t('當前庫存') :
             key === 'adjustmentQuantity' ? t('調整數量') :
             key === 'newQuantity' ? t('調整後') :
             key === 'reason' ? t('調整原因') :
             key === 'notes' ? t('備註') :
             key === 'requestedBy' ? t('申請人') :
             key === 'requestedAt' ? t('申請時間') :
             key === 'reviewedBy' ? t('審核人') :
             key === 'reviewedAt' ? t('審核時間') :
             key === 'urgency' ? t('緊急度') :
             key === 'status' ? t('狀態') :
             key === 'actions' ? t('操作') : key}
          </Checkbox>
        </Menu.Item>
      ))}
      <Menu.Divider />
      <Menu.Item key="showAll" onClick={showAllAdjustmentColumns}>
        <Button type="link" size="small" style={{ padding: 0 }}>
          {t('顯示所有列')}
                          </Button>
      </Menu.Item>
      <Menu.Item key="reset" onClick={resetAdjustmentColumnSettings}>
        <Button type="link" size="small" style={{ padding: 0 }}>
          {t('重置列設置')}
                          </Button>
      </Menu.Item>
    </Menu>
  );

  // 處理調整申請數據排序
  const sortedAndFilteredAdjustments = useMemo(() => {
    let result = [...adjustments];
    
    if (adjustmentSortField && adjustmentSortOrder) {
      result.sort((a, b) => {
        let aValue: any = a[adjustmentSortField as keyof StockAdjustment];
        let bValue: any = b[adjustmentSortField as keyof StockAdjustment];
        
        // 處理數字字段排序
        if (['currentQuantity', 'adjustmentQuantity', 'newQuantity'].includes(adjustmentSortField)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // 處理字符串字段排序
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) return adjustmentSortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return adjustmentSortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [adjustments, adjustmentSortField, adjustmentSortOrder]);

  // 統計數據
  const statistics = {
    totalItems: stockData.length,
    totalValue: stockData.reduce((sum, item) => sum + item.totalValue, 0),
    lowStockItems: stockData.filter(item => item.status === 'low_stock').length,
    outOfStockItems: stockData.filter(item => item.status === 'out_of_stock').length,
    totalCBM: stockData.reduce((sum, item) => sum + item.occupiedCBM, 0)
  };

  // Stock Adjustment統計數據
  const adjustmentStatistics = {
    total: adjustments.length,
    pending: adjustments.filter(item => item.status === 'pending').length,
    approved: adjustments.filter(item => item.status === 'approved').length,
    rejected: adjustments.filter(item => item.status === 'rejected').length,
    critical: adjustments.filter(item => item.urgency === 'critical').length
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 標題和展開收起按鈕 */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>{t('庫存管理')}</Title>
          <Text type="secondary">{t('管理和監控倉庫庫存狀態及調整申請')}</Text>
        </div>
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={toggleCollapsed}
          style={{ fontSize: 16 }}
        />
      </div>

      {/* KPI 儀表板（根據當前Tab切換） */}
      {!collapsed && (
        activeTab === 'stock' ? (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('總SKU數')} value={statistics.totalItems} prefix={<StockOutlined />} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('總庫存價值')} value={`$${statistics.totalValue.toFixed(2)}`} prefix="$" />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('庫存不足')} value={statistics.lowStockItems} prefix={<BarChartOutlined />} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('總佔用CBM')} value={`${statistics.totalCBM.toFixed(2)}m³`} prefix={<BarChartOutlined />} />
              </Card>
            </Col>
          </Row>
        ) : (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('總申請數')} value={adjustmentStatistics.total} prefix={<AuditOutlined />} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('待審核')} value={adjustmentStatistics.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#faad14' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('已批準')} value={adjustmentStatistics.approved} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col span={6}>
              <Card hoverable>
                <Statistic title={t('緊急申請')} value={adjustmentStatistics.critical} prefix={<ExclamationCircleOutlined />} valueStyle={{ color: '#ff4d4f' }} />
              </Card>
            </Col>
          </Row>
        )
      )}
      {/* 已移除頂部刷新操作欄，導出功能移至下方操作欄 */}

      {/* 標籤頁 */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={
          <span>
            <StockOutlined />
            {t('庫存管理')}
                                </span>
        } key="stock">
          {/* 庫存篩選欄 */}
          <Card style={{ marginBottom: '20px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <Search
                  placeholder={t('搜索SKU、產品名稱或條碼')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={setSearchText}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('客戶')}
                  value={selectedCustomerName || undefined}
                  onChange={(val) => setSelectedCustomerName(val || '')}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {customersWithInventory.map((c) => (
                    <Option key={c.name} value={c.name}>{c.name}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('Store Code')}
                  value={selectedStoreCode || undefined}
                  onChange={(val) => setSelectedStoreCode(val || '')}
                  allowClear
                  showSearch
                  // Search over the label, NOT children: children here is a JSX
                  // array ({code} - {name}), and calling .toLowerCase() on it
                  // throws on every keystroke, crashing the page into the
                  // frozen loading state.
                  optionFilterProp="label"
                >
                  {storeCodesWithInventory.map((s) => (
                    <Option key={s.storeCode} value={s.storeCode} label={`${s.storeCode} - ${s.customerName}`}>
                      {s.storeCode} - {s.customerName}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('庫存狀態')}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                >
                  <Option value="all">{t('全部狀態')}</Option>
                  <Option value="normal">{t('正常')}</Option>
                  <Option value="low_stock">{t('庫存不足')}</Option>
                  <Option value="out_of_stock">{t('缺貨')}</Option>
                  <Option value="excess">{t('庫存過多')}</Option>
                </Select>
              </Col>
            </Row>
          </Card>

          {/* 操作栏 */}
          <Card style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>{t('庫存記錄：{{total}} 條', { total: stockTotal })}</Text>
                {selectedStockIds.length > 0 && (
                  <Text type="secondary" style={{ marginLeft: 16 }}>
                    {t('已選擇 {{count}} 項', { count: selectedStockIds.length })}
                                                        </Text>
                )}
              </div>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadStock(1, 20)}
                >
                  {t('刷新數據')}
                                                  </Button>
                {!isCustomerRole && selectedStockIds.length > 0 && (
                  <Button
                    danger
                    icon={<ClearOutlined />}
                    onClick={handleOpenResetModal}
                  >
                    {t('一鍵清零 ({{count}})', { count: selectedStockIds.length })}
                  </Button>
                )}
                <DatePicker
                  value={asOfDate}
                  onChange={(d) => {
                    setAsOfDate(d);
                    // A time without a date means nothing.
                    if (!d) setAsOfTime(null);
                  }}
                  placeholder={t('作為日期 (Optional)')}
                  format="YYYY-MM-DD"
                  allowClear
                  disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                  style={{ width: 180 }}
                />
                <Tooltip
                  title={t('留空＝當日 23:59 的快照（含批號、庫位）。填上時間＝該時刻的庫存，按 SKU 匯總，不含批號/庫位。')}
                >
                  <TimePicker
                    value={asOfTime}
                    onChange={setAsOfTime}
                    disabled={!asOfDate}
                    placeholder={t('時間 (Optional)')}
                    format="HH:mm:ss"
                    allowClear
                    style={{ width: 130 }}
                  />
                </Tooltip>
                <Tooltip
                  title={t('盤點用：另加一行「帳差補計」，列出只存在於總帳、批號記錄已歸零的數量。盤點單若只依批號產生，會完全看不到這些 SKU。僅適用於即時庫存，選擇日期後停用。')}
                >
                  <Checkbox
                    checked={includeAggregateRemainder}
                    disabled={!!asOfDate}
                    onChange={(e) => setIncludeAggregateRemainder(e.target.checked)}
                  >
                    {t('盤點模式')}
                  </Checkbox>
                </Tooltip>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  type="default"
                >
                  {asOfDate
                    ? asOfTime
                      ? t('導出 ({{date}} {{time}})', {
                          date: asOfDate.format('YYYY-MM-DD'),
                          time: asOfTime.format('HH:mm:ss'),
                        })
                      : t('導出 ({{date}})', { date: asOfDate.format('YYYY-MM-DD') })
                    : t('導出')}
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportProductMovementSummary}
                  type="default"
                >
                  {t('報告')}
                                                  </Button>
                <Dropdown
                  overlay={stockColumnSettingsMenu}
                  trigger={['click']}
                  open={stockColumnSettingsVisible}
                  onOpenChange={setStockColumnSettingsVisible}
                >
                  <Button icon={<SettingOutlined />} title={t('列設置')}>
                    {t('列設置')}
                                                        </Button>
                </Dropdown>
              </Space>
            </div>
          </Card>

          {/* 庫存表格 */}
          <Card>
            <Table
              columns={draggableStockColumns as any}
              dataSource={sortedAndFilteredStockData}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1800, y: 600 }}
              rowSelection={!isCustomerRole ? {
                selectedRowKeys: selectedStockIds,
                onChange: handleStockSelectionChange,
              } : undefined}
              pagination={{
                current: stockCurrentPage,
                pageSize: stockPageSize,
                total: stockTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) => t('第 {{from}}-{{to}} 條，共 {{total}} 條記錄', { from: range[0], to: range[1], total }),
                onChange: (p, ps) => {
                  // 受控分頁：打 API 拉取
                  loadStock(p, ps);
                }
              }}
            />
          </Card>
        </TabPane>

        {/* CUSTOMER 角色不顯示庫存調整審核 Tab */}
        {!isCustomerRole && (
          <TabPane tab={
            <span>
              <AuditOutlined />
              {t('庫存調整審核')}
                                        {adjustmentStatistics.pending > 0 && (
                <Badge count={adjustmentStatistics.pending} style={{ marginLeft: 8 }} />
              )}
            </span>
          } key="adjustment">

          {/* 調整申請操作栏 */}
          <Card style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>{t('調整申請：{{total}} 條', { total: adjustmentTotal })}</Text>
                {selectedAdjustmentIds.length > 0 && (
                  <Text type="secondary" style={{ marginLeft: 16 }}>
                    {t('已選擇 {{count}} 項', { count: selectedAdjustmentIds.length })}
                                                            </Text>
                )}
              </div>
              <Space>
                {/* 查詢條件：SKU 與 狀態 */}
                <Input
                  allowClear
                  placeholder={t('按 調整單號/SKU/名稱 查詢')}
                  prefix={<SearchOutlined />}
                  value={adjustmentSearchSku}
                  onChange={(e) => setAdjustmentSearchSku(e.target.value)}
                  style={{ width: 260 }}
                />
                <Select
                  value={adjustmentStatus}
                  style={{ width: 140 }}
                  onChange={setAdjustmentStatus}
                >
                  <Option value="all">{t('全部狀態')}</Option>
                  <Option value="pending">{t('待審核')}</Option>
                  <Option value="approved">{t('已批准')}</Option>
                  <Option value="rejected">{t('已拒絕')}</Option>
                </Select>

                {!isCustomerRole && selectedAdjustmentIds.length > 0 && (
                  <>
                    <Button 
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleBatchApproval('approve')}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                      {t('批量批準 ({{count}})', { count: selectedAdjustmentIds.length })}
                    </Button>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => handleBatchApproval('reject')}
                    >
                      {t('批量拒絕 ({{count}})', { count: selectedAdjustmentIds.length })}
                    </Button>

                  </>
                )}
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => loadAdjustments(1, 20)}
                >
                  {t('刷新數據')}
                                                      </Button>
                <Dropdown overlay={adjustmentColumnSettingsMenu} trigger={["click"]}>
                  <Button icon={<SettingOutlined />} title={t('列設置')}>
                    {t('列設置')}
                                                            </Button>
                </Dropdown>
              </Space>
            </div>
          </Card>

          {/* 調整申請表格 */}
          <Card>
            <Table
              columns={draggableAdjustmentColumns as any}
              dataSource={sortedAndFilteredAdjustments}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1600, y: 600 }}
              rowSelection={!isCustomerRole ? {
                selectedRowKeys: selectedAdjustmentIds,
                onChange: handleBatchSelectionChange,
                getCheckboxProps: (record: StockAdjustment) => ({
                  disabled: record.status !== 'pending',
                  name: record.id,
                }),
              } : undefined}
              pagination={{
                current: adjustmentCurrentPage,
                pageSize: adjustmentPageSize,
                total: adjustmentTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                showTotal: (total, range) => t('第 {{from}}-{{to}} 條，共 {{total}} 條記錄', { from: range[0], to: range[1], total }),
                onChange: (p, ps) => {
                  loadAdjustments(p, ps);
                }
              }}
            />
          </Card>
        </TabPane>
        )}
      </Tabs>

      {/* 庫存詳情Modal */}
      <Modal
        title={
          <Space>
            <StockOutlined />
            <span>{t('庫存詳情 - {{sku}}', { sku: selectedStock?.sku ?? '' })}</span>
          </Space>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        closable
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            {t('關閉')}
                          </Button>
        ]}
        width={1000}
      >
        {selectedStock && (
          <Tabs defaultActiveKey="details">
            <TabPane tab={t('基本信息')} key="details">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="SKU">
                  {[selectedStock.sku, ...(selectedStock.alternateSkuList || [])].filter(Boolean).join(', ')}
                </Descriptions.Item>
                <Descriptions.Item label={t('條碼')}>{selectedStock.barcode}</Descriptions.Item>
                <Descriptions.Item label={t('產品名稱')} span={2}>{selectedStock.productName}</Descriptions.Item>
                <Descriptions.Item label={t('貨主賬號')}>{selectedStock.ownerAccount}</Descriptions.Item>
                <Descriptions.Item label={t('類別')}>{selectedStock.category || '-'}</Descriptions.Item>
                <Descriptions.Item label={t('可銷售數量 (Goods can be sold)')}>
                  <Space>
                    <Text style={{ fontSize: '16px', color: selectedStock.availableQuantity < 0 ? '#cf1322' : undefined }}>
                      {selectedStock.availableQuantity}
                    </Text>
                    {(selectedStock.oversellDebt || 0) > 0 && (
                      <Tooltip title={t('已確認超售 {{qty}} 件（未補回），可銷售 = 貨位庫存 − 超售欠貨', { qty: selectedStock.oversellDebt })}>
                        <Tag color="error">{t('超售欠貨')} {selectedStock.oversellDebt}</Tag>
                      </Tooltip>
                    )}
                    {(selectedStock.pendingReplacementQty || 0) > 0 && (
                      <Tooltip title={t('補發單已建立 {{qty}} 件（待處理），保留給欠貨客戶：可銷售 = 貨位庫存 − 補發中數量', { qty: selectedStock.pendingReplacementQty })}>
                        <Tag color="processing">{t('補發中')} {selectedStock.pendingReplacementQty}</Tag>
                      </Tooltip>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('總下單數量（PENDING）')}>
                  <Space>
                    {(selectedStock.totalOrderedQuantity || 0) > 0 ? (
                      <Tooltip title={t('點擊查看相關 PENDING 訂單')}>
                        <Text
                          style={{
                            fontSize: '16px',
                            fontWeight: (selectedStock.totalOrderedQuantity || 0) > selectedStock.availableQuantity ? 'bold' : 'normal',
                            color: '#1890ff',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                          onClick={() => {
                            navigate(`/inventory/outbound?itemSku=${encodeURIComponent(selectedStock.sku)}&status=待發貨`);
                          }}
                        >
                          {selectedStock.totalOrderedQuantity || 0}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text style={{ fontSize: '16px' }}>
                        {selectedStock.totalOrderedQuantity || 0}
                      </Text>
                    )}
                    {(selectedStock.totalOrderedQuantity || 0) > selectedStock.availableQuantity && (
                      <Tooltip title={t('警告：總下單數量({{ordered}})超過可銷售數量({{available}})，部分訂單將無法處理', { ordered: selectedStock.totalOrderedQuantity, available: selectedStock.availableQuantity })}>
                        <Tag color="error">{t('超出庫存')}</Tag>
                      </Tooltip>
                    )}
                    {(selectedStock.totalOrderedQuantity || 0) === 0 && (
                      <Tag color="default">{t('無待處理訂單')}</Tag>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t('出庫中數量')}>
                  {(selectedStock.outboundQuantity || 0) > 0 ? (
                    <Tooltip title={t('點擊查看相關「處理中」訂單（如需查看其他狀態，例如已包裝、待攬收，請在下一頁調整狀態篩選）')}>
                      <Text
                        style={{
                          fontSize: '16px',
                          color: '#1890ff',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onClick={() => {
                          navigate(`/inventory/outbound?itemSku=${encodeURIComponent(selectedStock.sku)}&status=處理中`);
                        }}
                      >
                        {selectedStock.outboundQuantity}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text style={{ fontSize: '16px' }}>{selectedStock.outboundQuantity}</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('佔用CBM')}>{selectedStock.occupiedCBM} m³</Descriptions.Item>
                <Descriptions.Item label={t('單價')}>${selectedStock.unitPrice}</Descriptions.Item>
                <Descriptions.Item label={t('總價值')}>${selectedStock.totalValue}</Descriptions.Item>
                <Descriptions.Item label={t('最後入庫單')}>{selectedStock.lastInboundOrder}</Descriptions.Item>
                <Descriptions.Item label={t('最後入庫數量')}>{selectedStock.lastInboundQuantity}</Descriptions.Item>
                <Descriptions.Item label={t('最後發貨單')}>{selectedStock.lastOutboundOrder}</Descriptions.Item>
                <Descriptions.Item label={t('最後發貨數量')}>{selectedStock.lastOutboundQuantity}</Descriptions.Item>
                <Descriptions.Item label={t('更新時間')} span={2}>{selectedStock.updateTime}</Descriptions.Item>
              </Descriptions>

              {/* 各倉庫庫存分佈 */}
              {selectedStock.warehouses && selectedStock.warehouses.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Title level={5}>{t('各倉庫庫存')}</Title>
                  <Table
                    dataSource={selectedStock.warehouses}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: t('倉庫'),
                        dataIndex: 'warehouseName',
                        key: 'warehouseName',
                        render: (text: string) => <Tag color="blue">{text}</Tag>
                      },
                      {
                        title: t('庫存數量'),
                        dataIndex: 'total',
                        key: 'total',
                        align: 'right' as const,
                        render: (value: number) => <Text strong>{value}</Text>
                      },
                      {
                        title: t('可用數量'),
                        dataIndex: 'available',
                        key: 'available',
                        align: 'right' as const
                      },
                      {
                        title: t('出庫中'),
                        dataIndex: 'shipping',
                        key: 'shipping',
                        align: 'right' as const
                      },
                      {
                        title: t('貨位'),
                        dataIndex: 'locationCode',
                        key: 'locationCode',
                        render: (text: string) => shortLocation(text) || '-'
                      },
                      {
                        title: t('更新時間'),
                        dataIndex: 'updatedAt',
                        key: 'updatedAt',
                        render: (text: string) => text ? new Date(text).toLocaleString('zh-TW') : '-'
                      }
                    ]}
                  />
                </div>
              )}
              
              {/* 貨位分佈 */}
              {productLocations.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Title level={5}>{t('貨位分佈')}</Title>
                  <Table
                    dataSource={productLocations}
                    rowKey={(record, index) => `${record.orderNumber}-${index}`}
                    pagination={false}
                    size="small"
                    columns={[
                      // {
                      //   title: '入庫單號',
                      //   dataIndex: 'orderNumber',
                      //   key: 'orderNumber',
                      //   width: 150
                      // },
                      {
                        title: t('批次'),
                        dataIndex: 'lotNumber',
                        key: 'lotNumber',
                        width: 120,
                        render: (text: string) => text || '-'
                      },
                      {
                        title: t('到期日'),
                        dataIndex: 'expirationDate',
                        key: 'expirationDate',
                        width: 120,
                        render: (date: string) => date ? new Date(date).toLocaleDateString('zh-TW') : '-'
                      },
                      {
                        title: t('貨位'),
                        dataIndex: 'locationPath',
                        key: 'locationPath',
                        render: (text: string) => {
                          if (text?.startsWith('[出庫中]')) {
                            return <Text strong style={{ color: '#722ed1' }}>📦 {text}</Text>;
                          }
                          return <Text strong style={{ color: '#1890ff' }}>{shortLocation(text)}</Text>;
                        }
                      },
                      {
                        title: t('庫存數量'),
                        dataIndex: 'quantity',
                        key: 'quantity',
                        width: 100,
                        align: 'right' as const,
                        render: (value: number) => <Text strong>{value}</Text>
                      }
                    ]}
                  />
                </div>
              )}
            </TabPane>
            
            <TabPane tab={t('相關訂單')} key="orders">
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <span>{t('類型篩選：')}</span>
                  <Select
                    value={relatedOrdersType}
                    onChange={handleRelatedOrdersTypeChange}
                    style={{ width: 120 }}
                    size="small"
                  >
                    <Option value="all">{t('全部')}</Option>
                    <Option value="outbound">{t('出庫訂單')}</Option>
                    <Option value="inbound">{t('入庫訂單')}</Option>
                    <Option value="adjustment">{t('庫存調整')}</Option>
                  </Select>
                </Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportRelatedOrders}
                  type="primary"
                  size="small"
                >
                  {t('導出')}
                                                  </Button>
              </div>
              <Table
                columns={orderColumns}
                dataSource={relatedOrders}
                rowKey="id"
                size="small"
                loading={relatedOrdersLoading}
                pagination={{
                  current: relatedOrdersPage,
                  pageSize: relatedOrdersPageSize,
                  total: relatedOrdersTotal,
                  onChange: handleRelatedOrdersPageChange,
                  showSizeChanger: true,
                  showTotal: (total) => t('共 {{total}} 筆', { total }),
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
              />
            </TabPane>
          </Tabs>
        )}
      </Modal>

      {/* 庫存調整Modal - 支持按位置調整 */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>{t('庫存調整申請')}</span>
          </Space>
        }
        open={adjustmentVisible}
        onCancel={() => {
          setAdjustmentVisible(false);
          adjustmentForm.resetFields();
          setLocationAdjustments({});
          setStockLocations([]);
        }}
        footer={null}
        width={900}
      >
        <Alert
          message={t('庫存調整申請')}
          description={
            <div>
              <p><strong>{t('調整流程：')}</strong>{t('製作者提交 → 核對者審核 → 系統執行')}</p>
              <p><strong>{t('操作說明：')}</strong></p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>{t('按位置調整（推薦）')}</strong>{t('：在下方表格中選擇具體位置進行調整')}</li>
                <li><strong>{t('正數')}</strong>{t('：增加庫存 (如 +10 表示增加10個)')}</li>
                <li><strong>{t('負數')}</strong>{t('：減少庫存 (如 -5 表示減少5個)')}</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <Form
          form={adjustmentForm}
          layout="vertical"
          onFinish={handleAdjustmentSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="SKU" name="sku">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('產品名稱')} name="productName">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          {/* 位置庫存調整表格 (新功能) */}
          {stockLocations.length > 0 && (
            <>
              <Divider>{t('按位置調整庫存')}</Divider>
              <Table
                dataSource={stockLocations}
                rowKey="inboundItemId"
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
                columns={[
                  {
                    title: t('倉庫'),
                    dataIndex: 'warehouseName',
                    width: 100,
                  },
                  {
                    title: t('位置'),
                    dataIndex: 'locationCode',
                    width: 150,
                    render: (code: string, record: any) => (
                      <Tooltip title={record.locationName || code}>
                        <span>{shortLocation(code) || record.locationId?.substring(0, 8) || '-'}</span>
                      </Tooltip>
                    )
                  },
                  {
                    title: t('批號'),
                    dataIndex: 'lotNumber',
                    width: 100,
                    render: (lot: string | null) => lot || '-'
                  },
                  {
                    title: t('到期日'),
                    dataIndex: 'expiryDate',
                    width: 100,
                    render: (date: string | null) => date ? moment(date).format('YYYY-MM-DD') : '-'
                  },
                  {
                    title: t('現有數量'),
                    dataIndex: 'currentStock',
                    width: 80,
                    align: 'right' as const,
                  },
                  {
                    title: t('調整數量'),
                    width: 120,
                    render: (_: any, record: any) => (
                      <InputNumber
                        size="small"
                        value={locationAdjustments[record.inboundItemId] || 0}
                        onChange={(value) => {
                          setLocationAdjustments(prev => ({
                            ...prev,
                            [record.inboundItemId]: value || 0
                          }));
                        }}
                        precision={0}
                        // A batch cannot give up more than it holds; the server
                        // rejects that too. Never above 0, so a batch that is
                        // already negative can still be corrected upward.
                        min={Math.min(0, -Number(record.currentStock || 0))}
                        style={{ width: '100%' }}
                      />
                    )
                  },
                  {
                    title: t('調整後'),
                    width: 80,
                    align: 'right' as const,
                    render: (_: any, record: any) => {
                      const adj = locationAdjustments[record.inboundItemId] || 0;
                      const newQty = record.currentStock + adj;
                      return (
                        <span style={{ color: adj !== 0 ? (adj > 0 ? '#52c41a' : '#ff4d4f') : undefined }}>
                          {newQty}
                        </span>
                      );
                    }
                  }
                ]}
                summary={() => {
                  const totalCurrent = stockLocations.reduce((sum, loc) => sum + loc.currentStock, 0);
                  const totalAdj = Object.values(locationAdjustments).reduce((sum, qty) => sum + qty, 0);
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <strong>{t('合計')}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <strong>{totalCurrent}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <strong style={{ color: totalAdj !== 0 ? (totalAdj > 0 ? '#52c41a' : '#ff4d4f') : undefined }}>
                            {totalAdj > 0 ? `+${totalAdj}` : totalAdj}
                          </strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <strong style={{ color: totalAdj !== 0 ? (totalAdj > 0 ? '#52c41a' : '#ff4d4f') : undefined }}>
                            {totalCurrent + totalAdj}
                          </strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              <Divider />
            </>
          )}

          {/* 無位置庫存時不允許調整 */}
          {stockLocations.length === 0 && (
            <>
              <Alert
                message={t('此產品暫無位置庫存記錄，無法進行庫存調整')}
                description={t('請先透過入庫功能建立位置庫存記錄，才能進行庫存調整。庫存調整必須指定具體的位置。')}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item label={t('當前庫存')} name="currentQuantity">
                <InputNumber disabled style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label={t('調整數量')}
                name="adjustmentQuantity"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  precision={0}
                  placeholder={t('無法調整 - 需要先建立位置庫存')}
                  disabled
                />
              </Form.Item>

              <Form.Item label={t('調整後數量')} name="newQuantity">
                <InputNumber disabled style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            label={t('調整原因')}
            name="reason"
            rules={[{ required: true, message: t('請選擇調整原因') }]}
          >
            <Select placeholder={t('請選擇調整原因')}>
              {adjustmentReasons.map(reason => (
                <Option key={reason.value} value={reason.value}>
                  {reason.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={t('備註說明')} name="notes">
            <Input.TextArea
              rows={3}
              placeholder={t('請詳細說明調整原因和相關情況')}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setAdjustmentVisible(false);
                adjustmentForm.resetFields();
                setLocationAdjustments({});
                setStockLocations([]);
              }}>
                {t('取消')}
                                            </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={adjustmentLoading}
                disabled={
                  stockLocations.length > 0
                    ? !Object.values(locationAdjustments).some(qty => qty !== 0)
                    : true // Disable when no locations - stock adjustment without location is not allowed
                }
              >
                {t('提交調整')}
                                            </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Stock Adjustment詳情Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>{t('調整申請詳情')}</span>
          </Space>
        }
        open={adjustmentDetailsVisible}
        onCancel={() => setAdjustmentDetailsVisible(false)}
        closable
        footer={[
          <Button key="close" onClick={() => setAdjustmentDetailsVisible(false)}>
            {t('關閉')}
                          </Button>
        ]}
        width={800}
      >
        {selectedAdjustment && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="SKU">
                {[selectedAdjustment.sku, ...(selectedAdjustment.alternateSkuList || [])].filter(Boolean).join(', ')}
              </Descriptions.Item>
              <Descriptions.Item label={t('產品名稱')}>{selectedAdjustment.productName}</Descriptions.Item>
              <Descriptions.Item label={t('倉庫')}>{selectedAdjustment.warehouse}</Descriptions.Item>
              <Descriptions.Item label={t('貨位')}>{shortLocation(selectedAdjustment.location)}</Descriptions.Item>
              <Descriptions.Item label={t('當前庫存')}>
                <Text strong style={{ fontSize: '16px' }}>{selectedAdjustment.currentQuantity}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('調整數量')}>
                <Text strong style={{ 
                  fontSize: '16px',
                  color: selectedAdjustment.adjustmentQuantity > 0 ? '#52c41a' : '#ff4d4f'
                }}>
                  {selectedAdjustment.adjustmentQuantity > 0 ? '+' : ''}{selectedAdjustment.adjustmentQuantity}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('調整後庫存')}>
                <Text strong style={{ fontSize: '16px' }}>{selectedAdjustment.newQuantity}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('緊急度')}>
                <Tag color={
                  selectedAdjustment.urgency === 'critical' ? 'red' :
                  selectedAdjustment.urgency === 'high' ? 'orange' :
                  selectedAdjustment.urgency === 'normal' ? 'blue' : 'green'
                }>
                  {selectedAdjustment.urgency === 'critical' ? t('緊急') :
                   selectedAdjustment.urgency === 'high' ? t('高') :
                   selectedAdjustment.urgency === 'normal' ? t('普通') : t('低')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('調整原因')} span={2}>
                <Tag>{selectedAdjustment.reason}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('備註說明')} span={2}>
                {selectedAdjustment.notes || t('無')}
              </Descriptions.Item>
            </Descriptions>

            <Divider>{t('審核流程')}</Divider>
            <Timeline>
              <Timeline.Item color="blue">
                <div>
                  <Text strong>{selectedAdjustment.requestedBy}</Text> {t('提交申請')}
                                                    <br />
                  <Text type="secondary">{selectedAdjustment.requestedAt}</Text>
                </div>
              </Timeline.Item>
              
              {selectedAdjustment.status !== 'pending' && (
                <Timeline.Item 
                  color={selectedAdjustment.status === 'approved' ? 'green' : 'red'}
                >
                  <div>
                    {selectedAdjustment.status === 'approved' ? t('{{user}} 批準了申請', { user: selectedAdjustment.reviewedBy }) : t('{{user}} 拒絕了申請', { user: selectedAdjustment.reviewedBy })}
                                                          <br />
                    <Text type="secondary">{selectedAdjustment.reviewedAt}</Text>
                    {selectedAdjustment.approvalReason && (
                      <>
                        <br />
                        <Text>{t('原因：')}{selectedAdjustment.approvalReason}</Text>
                      </>
                    )}
                  </div>
                </Timeline.Item>
              )}
              
              {selectedAdjustment.status === 'pending' && (
                <Timeline.Item color="gray">
                  <Text type="secondary">{t('等待審核...')}</Text>
                </Timeline.Item>
              )}
            </Timeline>
          </div>
        )}
      </Modal>

      {/* 審核Modal */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            <span>{t('審核調整申請')}</span>
          </Space>
        }
        open={approvalVisible}
        onCancel={() => {
          setApprovalVisible(false);
          approvalForm.resetFields();
          // 關閉彈窗後刷新列表，保留當前分頁與查詢條件
          loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);
        }}
        footer={null}
        width={500}
      >
        {selectedAdjustment && (
          <div>
            <Alert
              message={`${selectedAdjustment.sku} - ${selectedAdjustment.productName}`}
              description={t('申請調整庫存：{{current}} → {{next}} ({{sign}}{{delta}})', { current: selectedAdjustment.currentQuantity, next: selectedAdjustment.newQuantity, sign: selectedAdjustment.adjustmentQuantity > 0 ? '+' : '', delta: selectedAdjustment.adjustmentQuantity })}
              type="info"
              showIcon
              style={{ marginBottom: 20 }}
            />
            
            <Form
              form={approvalForm}
              layout="vertical"
              onFinish={handleApprovalSubmit}
            >
              <Form.Item name="action" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
              
              <Form.Item 
                label={t('審核意見')} 
                name="reason"
                rules={[{ required: true, message: t('請填寫審核意見') }]}
              >
                <Input.TextArea 
                  rows={4} 
                  placeholder={t('請詳細說明審核意見和理由')}
                />
              </Form.Item>
              
              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => {
                    setApprovalVisible(false);
                    approvalForm.resetFields();
                  }}>
                    {t('取消')}
                                                        </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    icon={approvalForm.getFieldValue('action') === 'approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    style={{ 
                      backgroundColor: approvalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f',
                      borderColor: approvalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f'
                    }}
                  >
                    {approvalForm.getFieldValue('action') === 'approve' ? t('批準申請') : t('拒絕申請')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 批量審核Modal */}
      <Modal
        title={
          <Space>
            <AuditOutlined />
            <span>{t('批量審核調整申請')}</span>
          </Space>
        }
        open={batchApprovalVisible}
        onCancel={() => {
          setBatchApprovalVisible(false);
          batchApprovalForm.resetFields();
          // 關閉彈窗後刷新列表，保留當前分頁與查詢條件
          loadAdjustments(adjustmentCurrentPage, adjustmentPageSize);
        }}
        footer={null}
        width={600}
      >
        <div>
          <Alert
            message={t('已選擇 {{count}} 個調整申請', { count: selectedAdjustmentIds.length })}
            description={t('請填寫審核意見，將應用於所有選中的申請')}
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
          
          <Form
            form={batchApprovalForm}
            layout="vertical"
            onFinish={handleBatchApprovalSubmit}
          >
            <Form.Item name="action" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            <Form.Item 
              label={t('審核意見')} 
              name="reason"
              rules={[{ required: true, message: t('請填寫審核意見') }]}
            >
              <Input.TextArea 
                rows={4} 
                placeholder={t('請詳細說明批量審核意見和理由')}
              />
            </Form.Item>
            
            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setBatchApprovalVisible(false);
                  batchApprovalForm.resetFields();
                }}>
                  {t('取消')}
                                                  </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  icon={batchApprovalForm.getFieldValue('action') === 'approve' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  style={{ 
                    backgroundColor: batchApprovalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f',
                    borderColor: batchApprovalForm.getFieldValue('action') === 'approve' ? '#52c41a' : '#ff4d4f'
                  }}
                >
                  {batchApprovalForm.getFieldValue('action') === 'approve' ? t('批量批準 ({{count}})', { count: selectedAdjustmentIds.length }) : t('批量拒絕 ({{count}})', { count: selectedAdjustmentIds.length })}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
      {/* 報告日期範圍選擇彈窗 */}
      <Modal
        title={t('選擇報告日期範圍')}
        open={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        onOk={() => reportForm.submit()}
        width={500}
      >
        <Form
          form={reportForm}
          layout="vertical"
          onFinish={handleGenerateReport}
          initialValues={{ dateType: 'created', onlyInOut: false }}
        >
          <Form.Item
            name="dateType"
            label={t('日期類型')}
            rules={[{ required: true, message: t('請選擇日期類型') }]}
          >
            <Radio.Group>
              <Radio value="created">{t('建立日期')}</Radio>
              <Radio value="pickup">{t('預計取貨日期 (Pickup Date)')}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="dateRange"
            label={t('日期範圍')}
            rules={[{ required: true, message: t('請選擇日期範圍') }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          {/* Off by default: leaving it unticked produces exactly the report
              that existed before this option was added. */}
          <Form.Item name="onlyInOut" valuePropName="checked" style={{ marginBottom: 4 }}>
            <Checkbox>{t('只包含出入庫記錄')}</Checkbox>
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginLeft: 24 }}>
            {t('不勾選則匯出與以往完全相同。')}
          </Text>
        </Form>
      </Modal>

      {/* 一鍵清零確認彈窗 */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>{t('確認清零庫存')}</span>
          </Space>
        }
        open={resetModalVisible}
        onCancel={() => setResetModalVisible(false)}
        onOk={handleBulkResetInventory}
        okText={t('確認清零')}
        cancelText={t('取消')}
        okButtonProps={{ danger: true, loading: resetLoading }}
        width={600}
      >
        <Alert
          message={t('警告：此操作不可逆！')}
          description={t('清零操作將刪除所選SKU的所有庫存記錄、庫存交易、庫存移動和批次記錄。入庫單和出庫單不會被刪除，但入庫單的 current_stock 將被重置為 0。')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div>
          <Text strong>{t('將要清零的SKU ({{count}} 個)：', { count: selectedStockIds.length })}</Text>
          <div style={{
            maxHeight: 200,
            overflowY: 'auto',
            marginTop: 8,
            padding: 8,
            backgroundColor: '#f5f5f5',
            borderRadius: 4
          }}>
            {stockData
              .filter(item => selectedStockIds.includes(item.id))
              .map(item => (
                <div key={item.id} style={{ padding: '4px 0', borderBottom: '1px solid #e8e8e8' }}>
                  <Text code>{item.sku}</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>{item.productName}</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {t('(庫存: {{stock}}, 可用: {{available}})', { stock: item.stockQuantity, available: item.availableQuantity })}
                  </Text>
                </div>
              ))
            }
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StockManagement;